import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import { createPortal } from 'react-dom';
import styled from 'styled-components';
// [ONE GLASS RECIPE 2026-09-01, user] One tooltip surface for the whole app — see TipGlass.
import { GLASS_BLUR, GLASS_RADIUS, glassBackground, glassBorder, glassInk, glassShadow } from '../components/TipGlass';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useRestorableState } from '../services/uiSnapshot'; // [SEAMLESS-UPDATE 2026-08-13]
import { fetchPayments, fetchFilters, fetchInBankSummary, updatePaymentWebsite, updatePaymentEmail, updatePaymentMapping, deletePayment, onDataChange, InBankSummary } from '../services/api';
import { useRealtime } from '../hooks/useRealtime'; // [REALTIME] 2026-06-23
import { useIsMobile } from '../hooks/useIsMobile'; // [MOBILE 2026-07-07]
import { usePresence } from '../hooks/usePresence'; // [MOTION-A 2026-08-20]
import AnimatedNumber from '../components/AnimatedNumber'; // [MOTION-B 2026-08-20]
import { fluid } from '../styles/motion'; // [MOTION-A 2026-08-20] [FLUID-1 2026-10-12]
import { fluidPop, fluidCascade } from '../components/Fluid'; // [FLUID-1 2026-10-12]
// [LIQUID-GLASS 2026-11-02, user] Add, Edit Data, the tags, the deals list and the inline edit box (In Bank Summary keeps
// its old look, user's call)
import {
  lgPress, lgGreen, lgPopover, lgButtonPrimary, LG_PRESS, LgPressProps,
} from '../styles/liquidGlass';
import {
  TagBadge, TagKindText, TagAmount, TagArrow, TagChevron, tagKindOf, OLD_DEPT_NAME, TAG_LABEL_SHAPE, tagLabelLook, tagLabelImage,
} from '../components/TagLabel'; // [TAG-STYLE 2026-11-03]
import { latestDataMonth } from '../utils/monthFilter';
import { Payment, PaginationMeta, SortConfig, Column, FiltersResponse } from '../types';
import SearchBar from '../components/SearchBar';
import DataTable from '../components/DataTable';
import { SkeletonBox } from '../components/Skeleton';
import MappingModal from '../components/MappingModal';
import DealCreationModal from '../components/DealCreationModal';
import AddPaymentModal from '../components/AddPaymentModal';
import GlassSelect from '../components/GlassSelect';
import EditColumnsMenu from '../components/EditColumnsMenu'; // [EDIT-COLUMNS 2026-09-28]
import { readPaymentExtraCols, writePaymentExtraCols } from '../utils/paymentExtraCols'; // [EDIT-COLUMNS 2026-09-28]
import { setWarmPaymentExtraCols } from '../services/prefetch'; // [EDIT-COLUMNS 2026-09-28]
import { toastSuccess, toastError } from '../components/toastService';
import { useSaveQueue, queuedToast } from '../hooks/useSaveQueue'; // [SAVE-QUEUE 2026-09-09]
import { pageRect, pageViewport } from '../utils/appZoom'; // [APP-FIT 2026-10-26] popups placed in page px
import { hasSharedDeal, uniqueDealIds } from '../utils/dealSlots'; // [DEAL-REUSE 2026-11-03]

const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* [STICKY-HEAD 2026-06-26] Fill the scroll card exactly and don't scroll HERE — the
     toolbar + In-Bank summary stay fixed and only the table area (below) scrolls. */
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: ${(p) => p.theme.surface};

  /* [PHONE-LAYOUT 2026-10-23, user] phones: NOTHING is fixed at the top — the whole page (title, search, filters, the In
     Bank Summary, the table) is ONE scroll, as Lead Management ("in the phone view nothing will be fixed on the top side
     otherwise how will i see the data for the below"). The table only swipes sideways (TableScroll below). */
  @media (max-width: 768px) {
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

// [STICKY-HEAD 2026-06-26] The FIXED top block — the Payments toolbar + In-Bank Summary. It
// sits OUTSIDE the scrolling table area (flex-shrink:0) so it never scrolls; only the table
// rows below it do. No vertical scrollbar runs through this region.
const FrozenTop = styled.div`
  flex-shrink: 0;
  background: ${(p) => p.theme.surface};
`;

// The scrolling table region — owns the vertical scroll (and horizontal on narrow screens).
// The DataTable's sticky <thead> pins to the top of THIS area (via parentScroll), so the
// column header stays just under the frozen block while rows scroll.
const TableScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: scroll; /* [SCROLLBAR 2026-07-08] always show the vertical scroller on desktop */
  /* [STICKY-HEAD 2026-06-26] The card no longer reserves a bottom strip for the
     floating dock on this page (AppLayout PageCard $flush), so the table fills the
     full card height — reclaiming ~2 rows that used to sit in dead space. The dock
     clearance lives HERE instead: a bottom pad INSIDE the scroll, so it costs space
     only at the very end of the scroll (keeping the last row above the dock), not as
     an always-visible gap. Matches the dock's reach (bottom:18px + ~68px tall). */
  padding-bottom: 88px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: a sideways swipe of the table stays inside this box (never the browser's
     back / forward gesture). */
  /* [PHONE-LAYOUT 2026-10-23, user] ...and it is as tall as its rows (no box of its own to scroll up / down in): it only
     swipes sideways, the page scrolls (Container). The 88 px end pad still lifts the last row above the dock. (Was, 10-22:
     the In Bank Summary rode inside this box with the toolbar frozen above it.) */
  @media (max-width: 768px) {
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    flex: none;
    min-height: 0;
    overflow-y: hidden;
    overscroll-behavior-y: auto;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  /* Soft glass strip — same family as the modals at low strength so the
     filter row reads as a floating bar above the table. */
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};
  flex-wrap: wrap;
  gap: 10px;

  /* Phones: stack title / search / filters vertically (each full-width) so the
     toolbar never has to cram everything onto one line. Desktop is unchanged. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
  @media (max-width: 600px) {
    padding: 10px 12px;
  }
`;

const PageTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;

  /* [PAYMENTS PHONE TITLE 2026-09-23, user] "make the payments heading come in the middle".
     Phones stack the toolbar full-width (see Toolbar), so centring the text centres the title;
     the search / filters / Add rows below keep their full-width layout. Desktop unchanged. */
  @media (max-width: 768px) {
    text-align: center;
    font-weight: 800; /* [PHONE-LAYOUT 2026-10-22] the phone page-head weight every tab uses */
  }
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  /* [MOBILE 2026-07-07] Phones: the two filter dropdowns (Month + Account) and the
     Add button share ONE row below the search+count row (see the render branch). The
     two selects split the width equally (1fr each) and the Add button sizes to its
     label (auto). min-width:0 lets the glass select triggers shrink below their 130px
     desktop floor so all three fit the phone width. Mirrors the New Sales toolbar. */
  @media (max-width: 768px) {
    display: grid;
    /* [PHONE-LAYOUT 2026-10-22] Two equal columns (was 1fr 1fr auto, which cut "October 2026" and "All Accounts"):
       Month | Account, then Tags across the whole row, then Edit Columns | Add as equal halves (admins only).
       Every trigger fills its cell and is 36px tall (a comfortable tap). On phones the children are the five
       buttons in that order (the record count sits on the search row), so the 3rd one is the Tags select. */
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
    gap: 8px;
    & > button { min-width: 0; width: 100%; min-height: 36px; }
    & > button:nth-child(3) { grid-column: 1 / -1; }
  }
`;

const RecordCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;

  /* [PHONE-LAYOUT 2026-10-22] Narrow phones (up to 380px): the count may take two short lines ("327 records" over
     "fetched") so the search field beside it keeps room for its whole "Search payments..." hint. */
  @media (max-width: 380px) {
    white-space: normal;
    max-width: 92px;
    font-size: 12px;
    line-height: 1.2;
    text-align: right;
  }
`;

// [MOBILE 2026-07-07] Phones only: the search field and the record count share one
// row (count pinned to the right by the inline search's flex:1 growth), with the two
// filter dropdowns + Add button on the row below. Desktop keeps search in the middle
// + count beside the filters. Mirrors the New Sales page.
const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

// Liquid-glass select shared across every page's filter toolbar.
const FilterSelect = GlassSelect;

/* [EDIT-COLUMNS 2026-09-28] One cell of an admin-added column: raw database value, read-only, on one
   line; a long value is cut with an ellipsis and shown in full on hover. */
const ExtraCellText = styled.span`
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
`;
/* [PAYMENT-MODE 2026-10-18, user] "it will be edit column and not on the payment tab all the time": Subscription / Manual
   is offered in Edit Columns (not a fixed column). It is not a database column, so its header gets a readable name; every
   other extra column keeps its exact database name. */
const EXTRA_COL_LABELS: Record<string, string> = { payment_mode: 'Subscription / Manual' };

const ExtraCell: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined || value === '') return <>—</>;
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return <ExtraCellText title={text}>{text}</ExtraCellText>;
};

/* [INV-SPLIT 2026-10-08, user] "One above the other": a website split between PMs shows one line per
   PM part in the PM Name and Placed Value cells (line i of each is the same part), not "A / B".
   A line keeps the cell's font, colour and centring, and cuts with its own "…" when the column is
   dragged narrow; data-dt-cut-inner gives the cut cell its full-text hover title (ColumnResize). */
const SplitLine = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
/** [INV-SPLIT 2026-10-08] The parts of a split website, in PM Name order; [] unless there are 2+. */
const splitPartsOf = (row: any): any[] =>
  (Array.isArray(row?.split_parts) && row.split_parts.length >= 2 ? row.split_parts : []);

/* [TAG-STYLE 2026-11-03, user] The tags are the "Clean label" of components/TagLabel (the manager: "This pill with dot is
   pure AI looking, please change this, make it better"); the old dot pill (TagCard / TagDot / DEPT_PALETTE) is gone.
   Colour = the type of money, an old brand is named after its amount, and a brand's three PIA fields are ONE tag. */
const tagMoney = (v: any) => `$${Number(v).toLocaleString()}`;

/** [TAG-STYLE 2026-11-03] A brand's PIA Amount, PIA Month (months paid) and PIA Dept (for Sales / PM) as ONE tag:
 *  "PIA $2,400 · 3 months · Sales". They are saved together (MappingModal); a missing part is left out. Live data on
 *  2026-11-03: months 2 to 12 or empty, for = Sales / PM, and only Scale Ranking has PIA. */
const piaTag = (row: any, dept: string) => {
  const has = (v: any) => v != null && v !== '' && v !== 0; // the tags' presence test (rowTagSet mirrors it)
  const amount = row[`${dept}_pia_amount`];
  const months = row[`${dept}_pia_month`];
  const forWho = row[`${dept}_pia_dept`];
  const n = Number(months);
  const note = [
    has(months) ? (Number.isFinite(n) ? `${n} ${n === 1 ? 'month' : 'months'}` : String(months)) : '',
    has(forWho) ? String(forWho) : '',
    OLD_DEPT_NAME[dept] ?? '',
  ].filter(Boolean).join(' · ');
  return (
    <TagBadge key={`${dept}_pia`} kind="other" label="PIA" value={has(amount) ? tagMoney(amount) : undefined} note={note || undefined} />
  );
};

/* [ROW-TAG 2026-08-21] Label-only badge for the auto-derived row_status stamp — same card
   chrome as every other tag, no value part (the stamp is a fact, not an amount). `title`
   explains where the stamp comes from, since no one sets it by hand any more.
   [ROW-PM-TAG 2026-08-25] The same badge now also marks the PM side of Team ROW (the server's
   `row_pm` flag: the managing PM was on Team ROW - PM in that month), so every Team ROW row
   filters together under the one 'ROW' tag. The title says which side (or both) applies —
   only the Sales side routes money to the ROW tile; the PM side is a view into Overall. */
/* [ROW-TAG-ANYWHERE 2026-09-29, user] A member of either ROW team ANYWHERE on the payment (PM name, deal
   owner, split owner) now gives the tag — server flags `row_pm` / `row_sales`. Titles say so. */
const ROW_TAG_TITLE = {
  // [AMAAN-ROW 2026-09-25] …or Amaan Sofi's deal was booked to the ROW side in the deal popup.
  // [VISHAL-ROW 2026-09-29] …or Vishal Suryavanshi's, the same way.
  // [ROW-CLICK-FINAL 2026-10-28] …and on their New Sale payments that answer alone decides the tag.
  sales: "A Team ROW - Sales member is on this payment this month (deal owner or PM), or the ROW or US popup was answered ROW (on Amaan Sofi's / Vishal Suryavanshi's New Sale deals that answer alone decides)",
  pm: "A Team ROW - PM member is on this payment this month (its PM or deal owner)",
  both: "Team ROW on both sides: a Team ROW - Sales member and a Team ROW - PM member are on this payment this month",
} as const;
const RowStatusTag: React.FC<{ side: keyof typeof ROW_TAG_TITLE }> = ({ side }) => (
  <TagBadge kind="row" label="ROW" title={ROW_TAG_TITLE[side]} />
);
/** The PM side of Team ROW, as computed by the server per payment month. */
const isRowPm = (row: any): boolean => Number(row?.row_pm) === 1;
/** [ROW-TAG-ANYWHERE 2026-09-29] The Sales side: the stored stamp, or a Team ROW - Sales member on the payment. */
const isRowSales = (row: any): boolean => row?.row_status === 'ROW' || Number(row?.row_sales) === 1;

const TagsWrapper = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 5px;
  padding: 4px 0;
  max-width: 260px;
  vertical-align: middle;
`;

// ── HubSpot deal deep-link ────────────────────────────────────────────────
// [HS-DEAL-LINK] 2026-06-23 — clicking the "Deal Created" tag opens the deal in
// HubSpot. The CRM record URL is:
//   https://app.hubspot.com/contacts/{portalId}/record/0-3/{dealId}
// where 0-3 is HubSpot's Deals object type and {dealId} is a value stored in
// payments.deals_record_id. Opening it in a new tab reuses whatever HubSpot
// session the browser is already signed into. The portal id is the company's
// single HubSpot account (same account the server's PAT token belongs to);
// overridable at build time via REACT_APP_HUBSPOT_PORTAL_ID.
const HUBSPOT_PORTAL_ID = process.env.REACT_APP_HUBSPOT_PORTAL_ID || '3835383';
const hubspotDealUrl = (dealId: string) =>
  `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/record/0-3/${dealId}`;
const openHubspotDeal = (dealId: string) => {
  window.open(hubspotDealUrl(dealId), '_blank', 'noopener,noreferrer');
};

// Clickable variant of the HubSpot tag: the same label as the other tags, but a
// button so it reads as actionable.
/* [LIQUID-GLASS 2026-11-02, user] A press squishes it a little and glows where it was pressed (isolation keeps that light
   inside the tag, never over the frozen header).
   [TAG-STYLE 2026-11-03, user] Now the "Clean label" in HubSpot orange: a little stronger edge and tint than the static tags
   (it is the only tag you can click), more tint under the mouse, and an arrow (one deal) or the number of deals with a down
   arrow (2 or more). */
const HubspotTagButton = styled.button.attrs<LgPressProps>(LG_PRESS)`
  position: relative;
  isolation: isolate;
  font: inherit;
  appearance: none;
  -webkit-appearance: none;
  ${TAG_LABEL_SHAPE}
  cursor: pointer;
  ${(p) => tagLabelLook(p, 'hubspot', 0.12, 0.45)}
  ${lgPress}
  &:hover {
    border-color: rgba(255, 122, 89, 0.6);
    background-image: ${(p) => tagLabelImage(p, 'hubspot', 0.22)};
  }
  &:focus-visible { outline: 2px solid rgba(255,122,89,0.65); outline-offset: 1px; }

  /* [PHONE-LAYOUT 2026-10-26, user] Phones: a finger-sized tap area (about 35 px tall) around the 25 px tag, drawn by an
     invisible layer like Edit Data's, so the row keeps its height and the tag its look: 5 px up and down (the tag rows'
     gap), 2 px to the sides (the next tag is 5 px away). [LIQUID-GLASS 2026-11-02] Now ::before (::after is the press
     light). [TAG-STYLE 2026-11-03] The tag is 22 px now, so 6 px up and down keeps the tap area about 34 px. */
  @media (max-width: 768px) {
    &::before {
      content: '';
      position: absolute;
      inset: -6px -2px;
    }
  }
`;

// Transparent full-screen catcher so any outside click closes the popover.
const DealPopoverBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2500;
  background: transparent;
`;

// Fixed-positioned (portaled to <body>) so the scrollable table never clips it.
const DealPopover = styled.div<{ $open: boolean }>`
  position: fixed;
  z-index: 2501;
  min-width: 210px;
  max-width: 300px;
  padding: 6px;
  border-radius: 16px;
  /* [LIQUID-GLASS 2026-11-02, user] Glass with the light top edge, like the filter lists. [STALE-PRESS 2026-11-02, user]
     Without the live blur (lgPopover): it opens next to the table's buttons (styles/liquidGlass.ts says why). */
  ${lgPopover}
  /* [FLUID-1 2026-10-12, user] Grows down out of the tag on the spring (centred on it, hence the
     translateX base), the title and deal rows rising in one after another (components/Fluid.tsx);
     the close plays before it unmounts. Replaces the old 0.16 s dpPop keyframes. */
  ${(p) => fluidPop({ open: p.$open, base: 'translateX(-50%)', origin: '50% 0', from: 'translateY(-8px) scale(0.92)' })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 12) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
`;

const DealPopoverTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.theme.muted};
  padding: 5px 10px 6px;
`;

const DealItem = styled.button`
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 12px; /* [LIQUID-GLASS 2026-11-02] rounder rows, like the glass lists */
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  text-align: left;
  color: ${(p) => p.theme.text};
  transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1);

  .hs { color: #ff7a59; font-size: 13px; flex-shrink: 0; }
  .who {
    flex: 1;
    min-width: 0;
    font-weight: 600;
    font-size: 12.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ext { opacity: 0.45; font-size: 11px; flex-shrink: 0; }

  &:hover {
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,122,89,0.16)' : 'rgba(255,122,89,0.12)')};
  }
  &:hover .ext { opacity: 0.85; }
  &:focus-visible {
    outline: none;
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,122,89,0.22)' : 'rgba(255,122,89,0.16)')};
  }
`;

// The clickable HubSpot "Deal Created" tag. One deal → open it directly. Two or
// more (deals_record_id is comma-separated) → open a small popover listing each
// deal (labelled by its owner) so the user picks which to open. `owners` arrives
// index-aligned to `dealIds`, resolved by deal id — see ownersForDealIds.
const HubspotDealTag: React.FC<{ dealIds: string[]; owners: string[] }> = ({ dealIds, owners }) => {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const multi = dealIds.length > 1;

  // [HS-OWNER-TIP 2026-08-24] Hover tooltip: the action on the first line, the deal
  // owner(s) on the second - so the owner is readable straight off the Payments grid
  // without opening HubSpot. Owners come from the same comma-joined deal_owner the
  // popover uses; rows linked before deal_owner was stamped have none, and then the
  // tooltip stays a single line rather than showing an empty "Deal Owner:".
  // GlobalTooltip renders title with white-space: pre-line, so the newline is a real
  // line break (and the native box would honour it too if that host ever went away).
  const ownerNames = owners.map((o) => o.trim()).filter(Boolean);
  const tipTitle =
    (multi ? `${dealIds.length} deals — click to choose one` : 'Open deal in HubSpot') +
    (ownerNames.length
      ? `\n${ownerNames.length > 1 ? 'Deal Owners' : 'Deal Owner'}: ${ownerNames.join(', ')}`
      : '');

  const close = useCallback(() => setCoords(null), []);
  // [FLUID-1 2026-10-12] Keeps the popover mounted (at its last coords) through the 200 ms close.
  const popPres = usePresence(coords, fluid.outMs);
  const popCoords = popPres.value;

  // While the popover is open, any scroll/resize/Escape closes it so it can never
  // float detached from its tag (it's portaled out of the scrolling table).
  useEffect(() => {
    if (!coords) return;
    const onScroll = () => close();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('keydown', onKey);
    };
  }, [coords, close]);

  const toggleList = () => {
    if (coords) { close(); return; } // toggle
    const r = anchorRef.current ? pageRect(anchorRef.current) : null; // [APP-FIT 2026-10-26] page px, like the list's top / left
    if (r) setCoords({ top: r.bottom + 6, left: r.left + r.width / 2 });
  };
  // [FLUID-8 2026-10-12, user] The list of deals opens on the mouse PRESS, like the dropdowns. One deal still opens
  // HubSpot on the click (it leaves the page — a press can still be cancelled by sliding off). Touch / keyboard click.
  const tagPressedRef = useRef(false);
  const handlePointerDown = (e: React.PointerEvent) => {
    tagPressedRef.current = multi && isPlainMousePress(e);
    if (tagPressedRef.current) toggleList();
  };
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (clickEndsPress(tagPressedRef, e)) return; // the press already opened / closed the list
    if (!multi) { openHubspotDeal(dealIds[0]); return; }
    toggleList();
  };

  return (
    <>
      <HubspotTagButton
        ref={anchorRef}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
        aria-haspopup={multi ? 'menu' : undefined}
        aria-expanded={multi ? !!coords : undefined}
        title={tipTitle}
      >
        {/* [TAG-STYLE 2026-11-03] "HUBSPOT Deal ↗", or "HUBSPOT 2 deals ⌄" when a list opens */}
        <TagKindText $kind="hubspot">HubSpot</TagKindText>
        <TagAmount $kind="hubspot">
          {multi ? `${dealIds.length} deals` : 'Deal'}
          {multi ? <TagChevron /> : <TagArrow />}
        </TagAmount>
      </HubspotTagButton>
      {multi && popPres.mounted && popCoords && createPortal(
        <>
          {coords && <DealPopoverBackdrop onClick={close} />}
          <DealPopover $open={!!coords && popPres.entered} style={{ top: popCoords.top, left: popCoords.left }} onClick={(e) => e.stopPropagation()}>
            <DealPopoverTitle>{dealIds.length} deals — open in HubSpot</DealPopoverTitle>
            {dealIds.map((id, i) => (
              <DealItem
                key={id}
                onClick={() => { openHubspotDeal(id); close(); }}
                title={`Open deal ${id} in HubSpot`}
              >
                <i className="fab fa-hubspot hs" />
                <span className="who">{owners[i] || `Deal ${i + 1}`}</span>
                <i className="fas fa-arrow-up-right-from-square ext" />
              </DealItem>
            ))}
          </DealPopover>
        </>,
        document.body
      )}
    </>
  );
};

/**
 * [DEAL-OWNER-BY-ID 2026-08-24] Which owner belongs to which deal — resolved by DEAL ID, not by
 * position in a comma-joined string.
 *
 * THE BUG THIS REPLACES. `deal_owner` is written DEDUPED (routes/hubspot.ts builds it through a
 * `new Set(...)`), but it was read POSITIONALLY: deal i was labelled owners[i]. Two deals with
 * the same owner therefore collapse to ONE entry and the second deal falls off the end — it was
 * labelled "Deal 2" instead of the person's name. Measured on live data: 8 payments carry more
 * than one deal, 3 of them label a deal wrongly today. With two different owners where one
 * repeats, the same mechanism would print an actively WRONG name rather than a placeholder.
 *
 * `deal_splits` already holds the exact answer — {slot: [{dealId, owner, amount, sv}]} — written
 * at deal-creation time and used the same way by routes/hubspot.ts for the deal-confirm popup.
 * Keying on dealId makes position irrelevant, so the dedup on the write side can no longer
 * mislabel anything and does not need to change.
 *
 * ⚠ The stored owner is a fast LOCAL COPY, not the authority. A deal id never changes, so if the
 * deal is reassigned in HubSpot the id still resolves and the live read refines the name — the
 * same contract `sv` (Second Voice) already rides on in this column.
 *
 * Falls back to the old positional split for any id the layout does not cover: rows created
 * before deal_splits existed. Zero such rows today, kept so old data can never regress.
 */
function ownersForDealIds(row: any, dealIds: string[]): string[] {
  const positional = String(row.deal_owner || '').split(',').map((s: string) => s.trim());

  const byId = new Map<string, string>();
  let layout: any = row.deal_splits;
  // The column reaches us as a JSON string from the API and as an already-parsed object after a
  // local save, so accept both rather than depending on which path filled the row.
  if (typeof layout === 'string') {
    try { layout = JSON.parse(layout); } catch { layout = null; }
  }
  if (layout && typeof layout === 'object') {
    /* [PIA-DEAL 2026-09-09] Keyed by SLOT now, not just iterated for its parts, so a deal from
       the `pia_amount` slot can be labelled "Name (PIA)". A payment can carry a sale and a PIA
       at once with different owners, and on the grid those two names were indistinguishable.
       ⛔ The marker is added HERE, on the way to the screen, and never written to
       payments.deal_owner: that column is read as a REAL PERSON'S NAME by the upsell PM
       attribution in routes/inventory.ts (COALESCE(deal_owner, pm_name)), by Campaign History's
       sale-owner query, by the ROW-tag derivation, and positionally by DealCreationModal to
       pre-fill the owner dropdowns on re-open — where an unresolvable "Name (PIA)" would make
       the server reject the update. */
    for (const [slot, parts] of Object.entries(layout) as [string, any][]) {
      const isPia = slot === 'pia_amount';
      for (const part of parts || []) {
        const id = part && part.dealId != null ? String(part.dealId) : '';
        const owner = part && part.owner ? String(part.owner).trim() : '';
        if (id && owner) byId.set(id, isPia ? `${owner} (PIA)` : owner);
      }
    }
  }

  return dealIds.map((id, i) => byId.get(id) || positional[i] || '');
}

const MAPPING_FIELDS: { key: string; label: string; dept: string; isMoney: boolean }[] = [
  { key: 'ranking_retention', label: 'Retention', dept: 'ranking', isMoney: true },
  { key: 'ranking_r_new_sale', label: 'Recurring New Sale', dept: 'ranking', isMoney: true },
  { key: 'ranking_o_new_sale', label: 'One-Time New Sale', dept: 'ranking', isMoney: true },
  { key: 'ranking_r_so', label: 'Recurring Upsell', dept: 'ranking', isMoney: true },
  { key: 'ranking_o_so', label: 'One-Time Upsell', dept: 'ranking', isMoney: true },
  { key: 'ranking_pia_amount', label: 'PIA Amount', dept: 'ranking', isMoney: true },
  { key: 'ranking_pia_month', label: 'PIA Month', dept: 'ranking', isMoney: false },
  { key: 'ranking_pia_dept', label: 'PIA Dept', dept: 'ranking', isMoney: false },
  { key: 'creative_retention', label: 'Retention', dept: 'creative', isMoney: true },
  { key: 'creative_r_new_sale', label: 'Recurring New Sale', dept: 'creative', isMoney: true },
  { key: 'creative_o_new_sale', label: 'One-Time New Sale', dept: 'creative', isMoney: true },
  { key: 'creative_r_so', label: 'Recurring Upsell', dept: 'creative', isMoney: true },
  { key: 'creative_o_so', label: 'One-Time Upsell', dept: 'creative', isMoney: true },
  { key: 'creative_pia_amount', label: 'PIA Amount', dept: 'creative', isMoney: true },
  { key: 'creative_pia_month', label: 'PIA Month', dept: 'creative', isMoney: false },
  { key: 'creative_pia_dept', label: 'PIA Dept', dept: 'creative', isMoney: false },
  { key: 'performance_retention', label: 'Retention', dept: 'performance', isMoney: true },
  { key: 'performance_r_new_sale', label: 'Recurring New Sale', dept: 'performance', isMoney: true },
  { key: 'performance_o_new_sale', label: 'One-Time New Sale', dept: 'performance', isMoney: true },
  { key: 'performance_r_so', label: 'Recurring Upsell', dept: 'performance', isMoney: true },
  { key: 'performance_o_so', label: 'One-Time Upsell', dept: 'performance', isMoney: true },
  { key: 'performance_pia_amount', label: 'PIA Amount', dept: 'performance', isMoney: true },
  { key: 'performance_pia_month', label: 'PIA Month', dept: 'performance', isMoney: false },
  { key: 'performance_pia_dept', label: 'PIA Dept', dept: 'performance', isMoney: false },
  { key: 'tech_retention', label: 'Retention', dept: 'tech', isMoney: true },
  { key: 'tech_r_new_sale', label: 'Recurring New Sale', dept: 'tech', isMoney: true },
  { key: 'tech_o_new_sale', label: 'One-Time New Sale', dept: 'tech', isMoney: true },
  { key: 'tech_r_so', label: 'Recurring Upsell', dept: 'tech', isMoney: true },
  { key: 'tech_o_so', label: 'One-Time Upsell', dept: 'tech', isMoney: true },
  { key: 'tech_pia_amount', label: 'PIA Amount', dept: 'tech', isMoney: true },
  { key: 'tech_pia_month', label: 'PIA Month', dept: 'tech', isMoney: false },
  { key: 'tech_pia_dept', label: 'PIA Dept', dept: 'tech', isMoney: false },
];

/* [TAG-FILTER 2026-08-21] The Tags dropdown's world.

   Tags are not stored anywhere - every pill in the Tags column is DERIVED per row while
   rendering. So the filter derives them the same way, from the same fields, with the SAME
   presence test the pill renderer uses (`!= null && !== '' && !== 0`) - one source of truth,
   so a row can never show a pill the filter ignores or vice versa.

   Canonical, department-agnostic names: the four per-department variants of a category all
   collapse to one entry, and the three PIA pills (Amount / Month / Dept always saved
   together) collapse to 'PIA'. NO_TAGS catches rows with nothing mapped, no deal and no ROW
   stamp - typically the red unmapped rows, which makes it a genuinely useful filter. */
const TAG_FILTER_ORDER = [
  'ROW', 'Deal Created', 'Retention', 'Recurring New Sale', 'One-Time New Sale',
  'Recurring Upsell', 'One-Time Upsell', 'PIA', 'Recovery', 'Shopify', // [SHOPIFY 2026-10-19]
] as const;
const NO_TAGS = 'No tags';

function rowTagSet(row: any): Set<string> {
  const out = new Set<string>();
  // [ROW-PM-TAG 2026-08-25] Either side of Team ROW is the one 'ROW' tag — mirrors the renderer.
  if (isRowSales(row) || isRowPm(row)) out.add('ROW'); // [ROW-TAG-ANYWHERE 2026-09-29]
  if (row.deals_record_id) out.add('Deal Created');
  for (const f of MAPPING_FIELDS) {
    const v = row[f.key];
    if (v == null || v === '' || v === 0) continue; // mirror of the pill renderer's test
    out.add(f.label.startsWith('PIA') ? 'PIA' : f.label);
  }
  if (row.recovery != null && row.recovery !== '' && Number(row.recovery) !== 0) out.add('Recovery');
  if (row.shopify_amount != null && row.shopify_amount !== '' && Number(row.shopify_amount) !== 0) out.add('Shopify'); // [SHOPIFY 2026-10-19]
  return out;
}

// [BUTTON-STYLE 2026-09-30] The recipe itself now lives in styles/buttons (buttonPrimary).
// [LIQUID-GLASS 2026-11-02, user] The glossy green capsule (squish + press light), as tall as the glass filter buttons.
const AddButton = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary}
`;

const WebsiteInput = styled.input`
  width: 100%;
  /* [CELL-WIDTH 2026-06-26] Shrink to the cell instead of an input's default intrinsic
     (~20-char) width forcing a narrow column wider while editing. */
  min-width: 0;
  /* [SLIM-EDIT 2026-06-25 · taller box] A little vertical padding makes the editor box
     taller and easier to read; an equal NEGATIVE vertical margin cancels it out so the
     cell's content height — and therefore the row height — never changes on edit. The
     INSET box-shadow border traces the (now taller) box without adding any layout height. */
  border: none;
  border-radius: 8px;
  padding: 3px 8px;
  margin: -3px 0;
  font-size: 13px;
  color: ${(p) => p.theme.text};
  outline: none;
  box-sizing: border-box;
  /* [LIQUID-GLASS 2026-11-02, user] Pressed into the glass, with the soft green ring and glow of a box being typed in.
     Rings are box-shadows (no border), so the box's height — and the row's — stays the same. */
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.85)')};
  box-shadow:
    inset 0 0 0 1px color-mix(in srgb, ${lgGreen} 70%, transparent),
    inset 0 1px 2px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(22,25,23,0.14)')},
    0 0 0 3px color-mix(in srgb, ${lgGreen} 16%, transparent),
    0 0 14px color-mix(in srgb, ${lgGreen} 22%, transparent);
`;

// Self-contained inline-edit field. It holds the in-progress text in its OWN
// local state so typing never lifts to the parent — that is what keeps the whole
// payments table from re-rendering on every keystroke. The parent only learns the
// value on commit (Enter / blur); Esc cancels. `doneRef` guarantees we commit (or
// cancel) exactly once, even though removing the focused input fires a final blur.
const InlineEditInput: React.FC<{
  initialValue: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}> = ({ initialValue, onCommit, onCancel }) => {
  const [draft, setDraft] = useState(initialValue);
  const doneRef = useRef(false);
  const commit = () => { if (doneRef.current) return; doneRef.current = true; onCommit(draft); };
  const cancel = () => { if (doneRef.current) return; doneRef.current = true; onCancel(); };
  return (
    <WebsiteInput
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      }}
    />
  );
};

// [DEAL-CONFIRM 2026-06-23] The old "Data Saved Successfully! Do you want to
// create a Deal?" confirmation box (SuccessOverlay/SuccessBox/…) lived here. The
// deal-worthy save now opens DealCreationModal directly, and that modal carries
// its own "✓ Data saved" line + Cancel, so those styled components were removed.

// ── In Bank 2.0 — self-contained ──────────────────────────────────────────
// Everything In Bank 2.0 needs (columns, styled components) is defined here and pulls only
// from `bankSummary` (the in-bank-summary API). It has no dependency on the (now removed)
// legacy In Bank table. Every column's figure comes from bankFigures(), keyed by the label
// below — ⚠ the LABEL IS THE KEY, into that function and into IN_BANK_2_TOOLTIPS. Rename one
// in a single place and its tile silently goes blank: no build error, just an empty box.
const IN_BANK_2_COLUMNS: string[] = [
  'Gross In-Bank',
  'This Month Refund/Disputes',
  'Past Refund/Disputes',
  'Dispute Won/Recovery',
  'Retention',
  'Recurring Upsell',
  'One-Time Upsell',
  'Recurring New Sale',
  'One-Time New Sale',
  'PIA Amount',
  // [SHOPIFY 2026-10-19, user] Mapped Shopify payouts — on the Overall and USA rows (the ROW row shows a dash).
  'Shopify',
  /* [NO ROW COLUMN 2026-08-31, user] 'ROW' was here. It was dropped the moment its money
     moved into the four category boxes above, which left it reading $0.00 on the Overall and
     USA rows for every month. Team ROW's money is still visible in two places: the ROW row of
     this strip (its category boxes) and the ROW panel behind the toggle.
     ⚠ bankFigures() STILL COMPUTES the ROW figure — the ROW row's Need to Map and EOM are
     built from it. This list controls what is DISPLAYED, nothing else. */
  'Need to Map',
  'EOM',
];

/* [ROW PANEL REMOVED 2026-08-31, user] IN_BANK_2_ROW_COLUMNS lived here: the nine boxes of the
   ROW panel that sat behind the Overall/ROW toggle. Both are gone. Team ROW is now the third
   row of the main strip, which shows it in the SAME columns as Overall and USA rather than in
   a private set of its own - the row_* fields it read are still on the payload and are what
   bankFigures() builds that row from. */

/* ─────────────────────────────────────────────────────────────────────────────
   [3-ROW SUMMARY 2026-08-31, user] The In Bank Summary is three rows of the SAME boxes:
   Overall, then USA, then ROW.

   ⚠ THE ARITHMETIC CONTRACT, and the reason this is one function rather than three:
   USA + ROW = OVERALL, cell by cell, in TWELVE of the thirteen columns — the two derived ones
   (Need to Map, EOM) included. ROW is read from the row_* figures the server already publishes;
   USA is Overall MINUS ROW, never a separate query. Subtraction is what makes the identity true
   by construction instead of by coincidence — two independently-written SQL sums would drift
   the first time a rule changed on one side only.

   Every DISPLAYED column adds up, because the one that did not — ROW — is no longer displayed.
   Since 2026-08-31 the Overall row keeps every dollar in its natural category box (user: "from
   the row box remove the value of upsell and add it to the respective upsell box and same with
   the new sales thing"), which left the ROW box empty on the Overall and USA rows, and the user
   then had the column removed outright. The figure is still COMPUTED below: the ROW row's
   category sum is built on it, so deleting it from this function would break that row's EOM.

   ⚠ EOM AND NEED TO MAP NEVER MOVED THROUGH ANY OF IT. Overall's category sum is the same
   total it was before: what the sale/upsell boxes gained is exactly what the ROW box gave up.

   Which ROW test each figure uses is inherited, not chosen here: gross / refunds / disputes /
   recovery / PIA are EITHER-SIDE (deal owner on Team ROW - Sales, or the managing PM on
   Team ROW - PM that month), retention is PM-SIDE (it is the money the ROW panel has shown
   since 2026-08-19), and the sale/upsell money is row_total's own definition. A payment that
   straddles the two tests can therefore land its gross in ROW and a category in USA; the
   difference falls into Need to Map, which is a plug by design, and the row still adds up.

   ⚠ THE ROW ROW'S FOUR SALE/UPSELL BOXES ARE A BREAKDOWN OF ITS ROW BOX, NOT MONEY BESIDE IT
   (user: "in the row row it will be in the main box, recurring upsell and one time upsell",
   then "in the row row it will remain the same"). 1,819 + 1,700 + 700 + 350 = the 4,569 in its
   ROW box, shown twice on purpose so the row explains itself. So the ROW row's category sum
   counts row_total ONCE and ignores those four boxes — add them in and its EOM double-counts
   every ROW dollar.
   ───────────────────────────────────────────────────────────────────────────── */
type BankScope = 'overall' | 'usa' | 'row';

const BANK_SCOPES: BankScope[] = ['overall', 'usa', 'row'];

const BANK_SCOPE_LABEL: Record<BankScope, string> = {
  overall: 'Overall',
  usa: 'USA',
  row: 'ROW',
};

/** Every In Bank Summary figure for one scope, keyed by the column label. */
function bankFigures(s: InBankSummary, scope: BankScope): Record<string, number> {
  const n = (v: any) => Number(v) || 0;

  // Team ROW's share of each Overall figure, on the rules described above.
  const rowGross = n(s.row_amount_total);
  const rowThisRd = n(s.row_this_month_refunds_disputes);
  const rowPastRd = n(s.row_refunds_disputes);
  const rowWon = n(s.row_disputes_won);
  const rowRecovery = n(s.row_recovery);
  const rowRetention = n(s.row_pm_retention);
  const rowPia = n(s.row_pia_amount);
  const rowShopify = n(s.row_shopify_amount); // [SHOPIFY 2026-10-19] the ROW tag — 0 for a real Shopify payout
  const rowTotal = n(s.row_total);

  const pick = (overall: number, row: number) =>
    scope === 'overall' ? overall : scope === 'row' ? row : overall - row;

  const gross = pick(n(s.amount_total), rowGross);
  const thisRd = pick(n(s.this_month_refunds_disputes), rowThisRd);
  const pastRd = pick(n(s.refunds_disputes), rowPastRd);
  const won = pick(n(s.disputes_won), rowWon);
  const recovery = pick(n(s.recovery), rowRecovery);
  const retention = pick(n(s.retention), rowRetention);
  const pia = pick(n(s.pia_amount), rowPia);
  const shopify = pick(n(s.shopify_amount), rowShopify);
  // The Overall sale/upsell tiles already exclude every ROW dollar (it lives in row_total),
  // so USA equals Overall here and the ROW row shows its own breakdown instead.
  const recSale = pick(n(s.recurring_sale), n(s.row_recurring_sale));
  const otSale = pick(n(s.one_time_sale), n(s.row_one_time_sale));
  const recUpsell = pick(n(s.recurring_upsell), n(s.row_recurring_upsell));
  const otUpsell = pick(n(s.one_time_upsell), n(s.row_one_time_upsell));
  /* ⚠ NOT DISPLAYED any more (the ROW column was removed on 2026-08-31) but still LOAD-BEARING:
     the ROW row's category sum below is built on it. Zero outside that row, where the same
     money is inside the four category boxes instead. */
  const rowBox = scope === 'row' ? rowTotal : 0;

  /* The mapped-category sum behind Need to Map and EOM.
     Overall / USA: every category box, and NOT rowBox — the ROW money is already inside those
     boxes, so adding rowBox as well would count each ROW dollar twice.
     ROW: the reverse — its four sale/upsell boxes are a BREAKDOWN of its own rowBox, so the sum
     takes rowBox once and ignores them.
     Both readings produce the same Overall total as the pre-2026-08-31 formula did, which is
     why EOM and Need to Map are untouched by any of today's re-divisions. */
  const categorySum = scope === 'row'
    ? retention + pia + rowBox + recovery + shopify
    : retention + recSale + otSale + recUpsell + otUpsell + pia + recovery + shopify; // [SHOPIFY 2026-10-19] a category like the rest: Need to Map drops by it, EOM does not move

  const needToMap = (gross - thisRd) - categorySum;
  const eom = won + categorySum + needToMap - pastRd;

  return {
    'Gross In-Bank': gross,
    'This Month Refund/Disputes': thisRd,
    'Past Refund/Disputes': pastRd,
    'Dispute Won/Recovery': won + recovery,
    'Retention': retention,
    'Recurring Upsell': recUpsell,
    'One-Time Upsell': otUpsell,
    'Recurring New Sale': recSale,
    'One-Time New Sale': otSale,
    'PIA Amount': pia,
    'Shopify': shopify,
    'ROW': rowBox,
    'Need to Map': needToMap,
    'EOM': eom,
  };
}

// [IN-BANK TOOLTIPS 2026-06-24] Plain-language explanation for each summary
// metric. Surfaced via the cell's `title` attribute, which the app-wide
// GlobalTooltip renders as a themed glass capsule after a short hover dwell.
const IN_BANK_2_TOOLTIPS: Record<string, string> = {
  'Gross In-Bank': 'The amount received in bank this month', // [2026-08-31] the user's exact wording
  'This Month Refund/Disputes': 'Amount received this month that was refunded or disputed within the same month.',
  'Past Refund/Disputes': "Amount from an earlier month's payment that has since been refunded or disputed.",
  'Dispute Won/Recovery': 'The disputed amount won or recovered this month.',
  'Retention': 'The amount recovered this month from the current inventory.',
  'Recurring New Sale': 'New recurring sales brought in this month by the sales team.',
  'One-Time New Sale': 'New one-time sales brought in this month by the sales team.',
  /* [ROW INTO CATEGORIES 2026-08-31, user] These four now carry Team ROW's money too, so the
     tips say "all of it" rather than naming an exception — an Aug-2026 reader hunting for
     Sakshi Sharma's 700.00 of Team ROW upsell is exactly how this change came about, and it
     is in Recurring Upsell now. */
  'Recurring Upsell': 'Recurring upsells brought in this month by the PM team — all of them, Team ROW included.',
  'One-Time Upsell': 'One-time upsells brought in this month by the PM team — all of them, Team ROW included.',
  'PIA Amount': 'Paid in advance — amount received this month for future months.',
  'Shopify': 'Shopify payouts received this month (PayPal, from Shopify Inc.) — mapped to Shopify by themselves.', // [SHOPIFY 2026-10-19]
  /* [NO ROW COLUMN 2026-08-31] The 'ROW' entry went with the column. Kept out rather than left
     behind as a dead key so a future reader does not resurrect the box by adding the label back
     and finding a tooltip already waiting for it. */
  'Need to Map': 'Payments received this month that are still unmapped in the payments table.',
  'EOM': 'The end-of-month amount currently in the bank.',
};

// Dedicated styled components for In Bank 2.0 so it owns its styling. These
// are fully independent — the legacy In Bank table's InBank* components have
// been removed and In Bank 2.0 is unaffected.
const InBank2Section = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${(p) => p.theme.line};

  /* [PHONE-LAYOUT 2026-10-22] Phones: the 12px content padding every phone card uses. */
  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const InBank2Title = styled.h2`
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  /* The 10px used to live here; it moved to InBank2Header, which now owns the gap
     between this row and the figures below it. */
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  /* [LIQUID-GLASS 2026-11-02, user] The In Bank Summary keeps its OLD look ("mak this to the old layout only"): the
     glass title marker and glass rows were tried and reverted the same day. */
  &::before {
    content: '';
    width: 3px;
    height: 16px;
    background: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    border-radius: 2px;
  }
`;

/* [REGION-TOGGLE 2026-08-19] The heading row: title on the left, the USA / ROW switch
   hard right. Wraps rather than squashing, because this section also renders on mobile
   and the switch must never be pushed off the edge. */
const InBank2Header = styled.div`
  display: flex;
  align-items: center;
  /* [ROW-PANEL 2026-08-21] The switch stays at the FAR RIGHT. It was moved next to the title
     on request, seen on screen, and moved straight back - so leave it here. */
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 0 10px;
`;

/* [NO TOGGLE 2026-08-31, user] RegionSegmented / RegionThumb / RegionSeg (the Overall-vs-ROW
   switch, a deliberate copy of the Dialing Report's PM/Sales control) and the sliding
   InBank2Viewport / InBank2Track / InBank2Panel stack all lived here. They went with the
   toggle: the summary shows Overall, USA and ROW as three rows at once, so there is no second
   panel to slide to. The Dialing Report still has the original segmented control - this was
   the copy, and removing it does not touch that one. */


const InBank2Grid = styled.div<{ $cols?: number; $labelCol?: boolean }>`
  display: grid;
  /* [RESPONSIVE 2026-06-23] Was repeat($cols,1fr) with overflow:hidden, which
     forced 13 equal columns to shrink until the nowrap labels/values overflowed
     and collided across cell borders, then clipped at the right edge. Lay the
     cells out at a readable ~132px floor and scroll horizontally when they don't
     all fit instead of squashing them. */
  grid-auto-flow: column;
  grid-auto-columns: minmax(132px, 1fr);
  /* [ROW NAME INLINE 2026-08-31, user] One narrow EXPLICIT track for the row's name
     (Overall / USA / ROW), ahead of the money columns. Auto-flow still creates every
     column after it, so this leaves the 132px floor and the horizontal scrolling
     untouched — and the ROW panel's grid, which passes no props, is unaffected. */
  grid-template-columns: ${(p) => (p.$labelCol ? '78px' : 'none')};
  gap: 0;
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: hidden;

  /* [ONE-SCROLL 2026-09-28, user] Phone only: the row no longer scrolls by itself; InBank2Stack is
     the single scroll area. Every row gets the SAME explicit width, the name cell plus the 132px
     column floor per column (exactly the layout a phone showed before, never narrower than the
     card), so the three rows line up column for column and the labels wrap as they did. A width
     from max-content would size each row by its own longest text and pull the rows out of line.
     overflow hidden clips nothing at that width; it only keeps the rounded corners. The +2px is
     the 1px left and right border, which sits INSIDE the width under the app-wide border-box. */
  @media (max-width: 768px) {
    width: calc(${(p) => (p.$labelCol ? 78 : 0)}px + ${(p) => p.$cols ?? 0} * 132px + 2px);
    min-width: 100%;
    overflow: hidden;
  }
`;


const InBank2Cell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 8px;
  border-right: 1px solid ${(p) => p.theme.line};
  text-align: center;

  &:last-child {
    border-right: none;
  }
`;

/* [3-ROW SUMMARY 2026-08-31, user] The three stacked rows — Overall, USA, ROW.
   [ROW NAME INLINE 2026-08-31] The gap tightened from 8px to 6px when the names moved off the
   top of each row and into it: the stack lost three caption lines, so the rows can sit closer
   without the block reading as one undivided slab. */
const InBank2Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  /* [ONE-SCROLL 2026-09-28, user] "in phone view it has 3 scrolls, make it to one scroll only".
     Each row grid scrolls sideways on its own, so on a phone the Overall, USA and ROW rows each
     had a scrollbar and could drift out of line. Phone only: the STACK is the one scroll area
     and every row takes its full natural width (max-content, never narrower than the card), so
     the three rows swipe together. They share one column template, so they stay aligned column
     for column. The row width itself is set on InBank2Grid (it knows its column count).
     Desktop is unchanged. */
  @media (max-width: 768px) {
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    padding-bottom: 4px;
  }
`;

/* [ROW NAME INLINE 2026-08-31, user] The row's name, in its own narrow cell to the LEFT of
   Gross In-Bank — "so that in between all 3 rows the space will reduce". It replaces the
   caption line that used to sit ABOVE each row, which cost ~18px of height per row for one
   short word. Colour carries the meaning: green = the Overall book, violet = Team ROW (the
   same violet as the row_status tag), grey = USA. The tint separates it from the figures
   without a heading, and its right border continues the cell rhythm. */
const InBank2ScopeCell = styled.div<{ $scope: BankScope }>`
  display: flex;
  /* Centred both ways, like every money cell beside it. */
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 10px 8px;
  border-right: 1px solid ${(p) => p.theme.line};
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  white-space: nowrap;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.035)' : 'rgba(22,25,23,0.03)')};
  color: ${(p) => (p.$scope === 'overall'
    ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)
    : p.$scope === 'row'
      ? (p.theme.colorScheme === 'dark' ? '#c4b5fd' : '#6d28d9')
      : p.theme.muted)};

  /* [PHONE-LAYOUT 2026-10-22] Phones: 11px, the smallest label size on a phone (OVERALL still fits the 78px cell). */
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const InBank2Label = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.theme.muted};
  margin-bottom: 4px;
  /* [RESPONSIVE 2026-06-23] Was nowrap, which overflowed the cell for the longer
     labels; let them wrap to a second line inside the (now floored) cell. */
  white-space: normal;
  line-height: 1.2;

  /* [PHONE-LAYOUT 2026-10-22] Phones: 11px, the smallest label size on a phone; the longest word (REFUND/DISPUTES,
     about 109px) still fits the 132px cell. */
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

/* `$muted` is the dashed Need to Map cell on the USA / ROW rows — a placeholder, so it must not
   carry the weight of a figure. */
const InBank2Value = styled.span<{ $red?: boolean; $muted?: boolean }>`
  font-size: 15px;
  font-weight: ${(p) => (p.$muted ? 500 : 700)};
  color: ${(p) => (p.$red ? '#dc2626' : p.$muted ? p.theme.muted : p.theme.text)};
`;

// [IN-BANK TOOLTIPS 2026-06-24] Self-contained themed capsule for the summary
// tiles. We deliberately do NOT route these through the native `title` /
// GlobalTooltip path: GlobalTooltip strips the `title` on hover and restores it
// on pointerdown, which makes the browser's plain native tooltip flash in the
// moment a tile is clicked. This capsule is driven purely by hover state and
// never touches `title`, so clicking a tile leaves the themed tip untouched and
// the native one can never appear. Styling mirrors GlobalTooltip so the two look
// identical. Theme-aware, viewport-clamped, flips above when it would clip.
const InBankTipBox = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 6000;
  pointer-events: none;
  max-width: 260px;
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: left;

  padding: 8px 12px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.45;
  letter-spacing: 0.01em;

  /* [ONE GLASS RECIPE 2026-09-01, user] The comment above promises this "mirrors GlobalTooltip so
     the two look identical" — it stopped being true the moment that capsule moved to the shared
     glass, because these values were a hand-copy. Both now read the SAME exported recipe, so the
     promise holds by construction rather than by somebody remembering to update two places. */
  border-radius: ${GLASS_RADIUS}px;
  color: ${(p) => glassInk(p.theme.colorScheme === 'dark')};
  background: ${(p) => glassBackground(p.theme.colorScheme === 'dark')};
  backdrop-filter: ${GLASS_BLUR};
  -webkit-backdrop-filter: ${GLASS_BLUR};
  border: ${(p) => glassBorder(p.theme.colorScheme === 'dark')};
  box-shadow: ${(p) => glassShadow(p.theme.colorScheme === 'dark')};
`;

interface InBankTip {
  text: string;
  anchor: DOMRect;
}

// Positions the capsule under its anchor tile (pre-paint, so no flash), centred
// and clamped inside the viewport, flipping above when the bottom would clip.
const InBankInfoTip: React.FC<{ tip: InBankTip | null }> = ({ tip }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!tip || !box) return;
    // [APP-FIT 2026-10-26] In page px (utils/appZoom): the left / top set below are page px under the app zoom.
    const { width: bw, height: bh } = pageRect(box);
    const { width: vw, height: vh } = pageViewport();
    const anchor = pageRect(tip.anchor);
    const GAP = 8;
    const MARGIN = 8;
    let left = anchor.left + anchor.width / 2 - bw / 2;
    left = Math.max(MARGIN, Math.min(left, vw - bw - MARGIN));
    let top = anchor.bottom + GAP;
    if (top + bh > vh - MARGIN) {
      const above = anchor.top - GAP - bh;
      top = above >= MARGIN ? above : Math.max(MARGIN, Math.min(top, vh - bh - MARGIN));
    }
    box.style.left = `${Math.round(left)}px`;
    box.style.top = `${Math.round(top)}px`;
  }, [tip]);
  if (!tip) return null;
  return createPortal(<InBankTipBox ref={boxRef}>{tip.text}</InBankTipBox>, document.body);
};

/**
 * [PERF 2026-08-14] Locale formatters built ONCE at module scope.
 *
 * `toLocaleString`/`toLocaleDateString` WITH an options object construct a fresh Intl formatter
 * on every call. Measured on V8: money-with-options **34µs** and date-with-options **90µs** per
 * call, against 0.7µs and 1.5µs for a cached formatter — 50x and 60x. This page renders a whole
 * month of payments with several money columns plus a date column per row, so those calls land
 * in the thousands per table build.
 *
 * NOTE the deliberate asymmetry: `toLocaleString('en-US')` with NO options is already fast
 * (0.52µs — V8 fast-paths it) and caching it measures very slightly SLOWER, so the no-options
 * calls on this page are intentionally left exactly as they are.
 */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTH_LONG_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
// [DATE-STYLE 2026-09-29, user] One date style across the app: "Sep 8, 2026" (was "Sep 08, 2026").
const DATE_DMY_UTC_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

// Generic currency formatter used by In Bank 2.0.
function fmtBank(n: number) {
  // A value whose magnitude rounds to zero cents (a -1e-13 float residual from
  // summing many decimals, or a -0) must never render as "-$0.00" — collapse it to
  // a clean 0 first. Anything ≥ half a cent keeps its real sign (a genuine -$0.01
  // still shows the minus).
  if (Math.abs(n) < 0.005) n = 0;
  return `$${MONEY_FMT.format(n)}`;
}

/* [LIQUID-GLASS 2026-11-02, user] "make this button also in the same liquid glass thing": a small green-tinted glass
   capsule like Edit Mapping's "Fill rest" (light top edge, thin green ring, squish + press light — styles/liquidGlass.ts).
   25 px tall like the old outline button, so the rows keep their height. isolation keeps the press light's layer inside
   the button (never over the table's frozen header). */
const EditBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  position: relative;
  isolation: isolate;
  height: 25px;
  padding: 0 12px;
  border-radius: 999px;
  border: none;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  color: ${(p) => p.theme.greenInk};
  background-color: color-mix(in srgb, ${lgGreen} 14%, transparent);
  background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0) 60%);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, ${lgGreen} 40%, transparent), inset 0 1px 0 rgba(255, 255, 255, 0.25);
  white-space: nowrap;
  ${lgPress}

  &:hover:not(:disabled) {
    background-color: color-mix(in srgb, ${lgGreen} 22%, transparent);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    /* A disabled <button> swallows pointer events, so any title sits on the
       EditBtnWrap below instead; drop pointer-events here so the hover falls
       through to that wrapper and the GlobalTooltip can explain why mapping is
       blocked. */
    pointer-events: none;
    color: ${(p) => p.theme.muted};
    background-color: transparent;
    box-shadow: inset 0 0 0 1px color-mix(in srgb, ${(p) => p.theme.muted} 40%, transparent);
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: a finger-sized tap area (about 37px tall) around the 25px button, drawn by an
     invisible layer, so the row keeps its height and the button its look. [LIQUID-GLASS 2026-11-02] Now the ::before
     layer: ::after is the press light. */
  @media (max-width: 768px) {
    &::before {
      content: '';
      position: absolute;
      inset: -6px -4px;
    }
  }
`;

// Hover host for the Edit Data button. The button itself is pointer-events:none
// while disabled (see above), so this wrapper carries the not-allowed cursor and
// the `title` that the app-wide GlobalTooltip renders as a themed capsule —
// telling the user exactly why the row can't be mapped yet.
const EditBtnWrap = styled.span<{ $disabled?: boolean }>`
  display: inline-flex;
  ${(p) => (p.$disabled ? 'cursor: not-allowed;' : '')}
`;

// Current month in YYYY-MM format.
// [EFFECTIVE-MONTH 2026-07-01] Delegates to the app-wide effective current month
// (12:30 PM IST rollover on the 1st) via latestDataMonth so the default view AND the
// non-admin edit lock roll over in lockstep with the rest of the app — not at local
// midnight, which would wrongly lock the (still-current) previous month for 12.5h.
function getCurrentMonth(): string {
  return latestDataMonth();
}

// Format YYYY-MM to readable label.
// [MONTH-FULL-NAME] 2026-06-23 — full month name (e.g. "June 2026") to match the
// EOM page. Used only by the month filter dropdown, so 'long' is safe.
function formatMonthLabel(m: string): string {
  const [year, month] = m.split('-');
  return MONTH_LONG_FMT.format(new Date(parseInt(year), parseInt(month) - 1));
}

const baseColumns: Omit<Column, 'render'>[] = [
  { key: 'payment_date', label: 'Payment Date', headerNoWrap: true },
  { key: 'website', label: 'Website' },
  { key: 'email', label: 'Email' },
  { key: 'amount', label: 'Amount', align: 'center' as const },
  { key: 'account_name', label: 'Account', align: 'center' as const },
  { key: 'refund_dispute', label: 'Refund/Dispute', align: 'center' as const },
  { key: 'pm_name', label: 'PM Name', align: 'center' as const },
  { key: 'placed_value', label: 'Placed Value', align: 'center' as const },
  { key: 'tags', label: 'Tags', align: 'center' as const },
  { key: 'edit_data', label: 'Edit Data', align: 'center' as const },
];

// Normalize a website to a comparison key (strip protocol / www / path / trailing dots)
// so the same site spelled differently still buckets together.
const normWebsite = (s: string | null | undefined) => (s || '').trim().toLowerCase()
  .replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '').replace(/\.+$/, '');

// (website, month) bucket key — groups a payment with its same-site, same-month
// siblings. Used to surface how prior payments for the bucket were mapped.
/* ── [DEAL-MISSING 2026-08-05] "mapped, but nobody created the deal" ──────────
 * A payment mapped to any of the four deal slots should have a HubSpot deal. When it
 * doesn't, the row goes RED like an unmapped one, so it's visible as work outstanding —
 * open it and create the deal.
 *
 * ONLY from June 2026. Before that the team used a different tool whose deals were never
 * linked back here, so flagging those months would light up ~90 historical rows that are
 * perfectly fine. (171 of them were back-linked on 2026-08-05; the rest either have no
 * HubSpot company at all or are one-deal-covers-several-payments cases that can't be
 * linked 1:1.)
 */
const DEAL_FLAG_FROM_MONTH = '2026-06';
const DEAL_SLOTS = ['r_new_sale', 'o_new_sale', 'r_so', 'o_so'] as const;
const DEAL_DEPTS = ['ranking', 'creative', 'performance', 'tech'] as const;

const slotTotal = (row: any, slot: string): number =>
  DEAL_DEPTS.reduce((sum, d) => sum + (Number(row[`${d}_${slot}`]) || 0), 0);

export const needsDeal = (row: any): boolean => {
  const month = String(row.month_str || '').slice(0, 7);
  if (!month || month < DEAL_FLAG_FROM_MONTH) return false;

  const mappedSlots = DEAL_SLOTS.filter((s) => slotTotal(row, s) > 0);
  if (mappedSlots.length === 0) return false;          // retention-only etc. — no deal expected

  const ids = String(row.deals_record_id || '').split(',').map((s) => s.trim()).filter(Boolean);
  if (ids.length === 0) return true;                   // mapped for a deal, no deal at all
  // [DEAL-REUSE 2026-11-03, user] The same deal twice = two deal types on ONE HubSpot deal, so one of them has no deal
  // (aciminla.com, 31 Oct: New Sale + Upsell both on the upsell's deal). Server twin: sharedDealIdSql (services/dealSlots.ts).
  if (hasSharedDeal(row.deals_record_id)) return true;

  // A deal EXISTS but we only know WHICH SLOT it covers from deal_splits. Without that
  // layout we cannot tell, so we say nothing rather than paint a healthy row red.
  if (!row.deal_splits) return false;
  let layout: Record<string, unknown[]>;
  try {
    layout = typeof row.deal_splits === 'string' ? JSON.parse(row.deal_splits) : row.deal_splits;
  } catch {
    return false;
  }
  if (!layout || typeof layout !== 'object') return false;
  return mappedSlots.some((s) => !Array.isArray(layout[s]) || layout[s].length === 0);
};

const bucketKey = (row: { website?: string | null; month_str?: string | null }) =>
  `${normWebsite(row.website)}|${row.month_str ?? ''}`;

/* ── [WRITE-LATENCY 2026-08-25] Optimistic-save helpers ─────────────────────────────────
   The table used to learn a row's new colour and position only from the server: the mapping
   PUT (2-3 s — every DB round trip is ~270 ms and the route chained ten) and then a full
   list reload (~1 s). Both facts can be computed here from the same rules the DB and the
   list route use, so the row moves the moment Save is pressed and the reply only confirms
   it. Keep these in step with:
     • payments.total_amount (generated) = the 24 dollar buckets + recovery + shopify_amount, and
       payments.mapped_status (generated) = (total_amount = final_amount);
     • routes/payments.ts GET / ORDER BY: needs-attention rows (unmapped, or mapped with no
       deal) first, then the chosen column and direction. */
const MAPPED_DOLLAR_KEYS = DEAL_DEPTS.flatMap((d) =>
  ['retention', 'r_new_sale', 'o_new_sale', 'r_so', 'o_so', 'pia_amount'].map((s) => `${d}_${s}`));
const round2 = (n: number) => Math.round(n * 100) / 100;
/** The DB's generated mapped_status, computed from the row's (possibly just-typed) values. */
const optimisticMappedStatus = (row: any): 'TRUE' | 'FALSE' => {
  let total = 0;
  for (const k of MAPPED_DOLLAR_KEYS) total += Number(row[k]) || 0;
  total += Number(row.recovery) || 0;
  total += Number(row.shopify_amount) || 0; // [SHOPIFY 2026-10-19] inside total_amount too
  return round2(total) === round2(Number(row.final_amount) || 0) ? 'TRUE' : 'FALSE';
};
/** 0 = needs attention (sorts first), 1 = settled — the list route's CASE expression. */
const attentionRank = (row: any): 0 | 1 => (row.mapped_status === 'TRUE' && !needsDeal(row) ? 1 : 0);
const compareSortValue = (a: any, b: any, sortBy: string, sortOrder: string): number => {
  const av = a?.[sortBy];
  const bv = b?.[sortBy];
  if (av == null && bv == null) return 0;
  if (av == null) return 1; // blanks last either way; an exact tie is settled by the next live refresh
  if (bv == null) return -1;
  const an = typeof av === 'number' ? av : Number(av);
  const bn = typeof bv === 'number' ? bv : Number(bv);
  const bothNumeric = av !== '' && bv !== '' && !Number.isNaN(an) && !Number.isNaN(bn);
  const cmp = bothNumeric ? an - bn : String(av).localeCompare(String(bv)); // ISO dates compare as text
  return sortOrder === 'asc' ? cmp : -cmp;
};
/** Stable: rows that compare equal keep the order the server gave them. */
const resortRows = (rows: any[], sortBy: string, sortOrder: string): any[] =>
  rows.slice().sort((a, b) => (attentionRank(a) - attentionRank(b)) || compareSortValue(a, b, sortBy, sortOrder));

// [MAP-VALIDATION] 2026-06-23 — true when a mapping-save payload carries NO
// allocation at all: no monetary value, no Recovery, no ROW flag, no PIA
// Month/Dept. The Edit Mapping modal blocks an empty Save on a never-mapped
// payment, so an empty payload only reaches the save path when an already-mapped
// payment was deliberately cleared (Clear → Save) — i.e. an un-map. Drives the
// "Payment unmapped successfully" confirmation.
const isUnmapPayload = (f: Record<string, any>): boolean => {
  if (f.row_status === 'ROW') return false;
  if (f.recovery != null && Number(f.recovery) !== 0) return false;
  if (f.shopify_amount != null && Number(f.shopify_amount) !== 0) return false; // [SHOPIFY 2026-10-19]
  for (const mf of MAPPING_FIELDS) {
    const v = f[mf.key];
    if (v == null || v === '') continue;
    if (mf.isMoney) { if (Number(v) !== 0) return false; }
    else return false; // a PIA Month/Dept selection counts as a mapping
  }
  return true;
};

const PaymentsPage: React.FC = () => {
  const { isDark } = useTheme();
  // [MOBILE 2026-07-07] Drives the toolbar reflow (search+count on one row, the two
  // dropdowns + Add on the next) and keeps the REAL desktop table on phones.
  const isMobile = useIsMobile();
  // The "Add" payment action is admin-only. Non-admins viewing this page never
  // see the button (and the backend POST /api/payments gate enforces it too).
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  /* [EDIT-COLUMNS 2026-09-28, user] Admin-only extra table columns (the Edit Columns picker), added
     after Placed Value and remembered per admin in this browser (the user's choice). Non-admins never
     send or render them, and the server ignores them for a non-admin anyway. */
  const [extraCols, setExtraCols] = useState<string[]>(() => readPaymentExtraCols(user?.id));
  // The names the server confirmed it can show on the last load (null = not known yet, or it could
  // not check). Only these are drawn, so a saved name the database no longer has never shows up as
  // an empty column.
  const [acceptedExtraCols, setAcceptedExtraCols] = useState<string[] | null>(null);
  const activeExtraCols = useMemo(() => (isAdmin ? extraCols : []), [isAdmin, extraCols]);
  // A change of columns reloads the rows SILENTLY: the table stays on screen, no skeletons.
  const extraColsSilentRef = useRef(false);
  const saveExtraCols = useCallback((next: string[]) => {
    setExtraCols(next);
    writePaymentExtraCols(user?.id, next);
    setWarmPaymentExtraCols(next);
  }, [user?.id]);
  const applyExtraCols = useCallback((next: string[]) => {
    if (next.join(',') === extraCols.join(',')) return; // nothing changed: no reload
    extraColsSilentRef.current = true;
    saveExtraCols(next);
  }, [extraCols, saveExtraCols]);
  // Forget saved names the server rejected (a column dropped or renamed in the database). Only runs on
  // a real answer: when the server could not check, acceptedExtraCols is null and nothing is dropped.
  useEffect(() => {
    if (!acceptedExtraCols || !extraCols.length) return;
    const ok = new Set(acceptedExtraCols);
    const kept = extraCols.filter((n) => ok.has(n));
    if (kept.length !== extraCols.length) saveExtraCols(kept);
  }, [acceptedExtraCols]); // react to a new server answer only
  // Previous-month lock: non-admins may only edit the current calendar month and
  // later (website cell + Edit Data). A row in an earlier month is read-only for
  // them; admins are unrestricted. row.month_str is 'YYYY-MM-DD' (first of the
  // row's month); compare its YYYY-MM against the current month. The backend
  // enforces the same rule (PUT /payments/:id and /:id/mapping return 403).
  const currentMonth = getCurrentMonth();
  const isMonthLocked = useCallback(
    (row: { month_str?: string | null }): boolean =>
      !isAdmin && !!row?.month_str && String(row.month_str).slice(0, 7) < currentMonth,
    [isAdmin, currentMonth]
  );
  const [data, setData] = useState<Payment[]>([]);
  // pageSize: 0 tells the API to return every matching row (pagination removed).
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    pageSize: 0,
    totalRows: 0,
    totalPages: 0,
  });
  const [sort, setSort] = useRestorableState<SortConfig>('payments.sort', { sortBy: 'payment_date', sortOrder: 'desc' });
  const [search, setSearch] = useRestorableState('payments.search', '');
  // [MULTI-MONTH 2026-07-15] The month filter is now MULTI-select: `months` holds
  // every ticked month ([] = "All Months"). `month` is kept as the derived
  // single-month value — it's exactly months[0] when one month is selected, and ''
  // otherwise — so every existing consumer below (editability lock, Add Payment
  // default, filters fetch) keeps working unchanged against a single month.
  const [months, setMonths] = useRestorableState<string[]>('payments.months', [getCurrentMonth()]);
  const month = months.length === 1 ? months[0] : '';
  // Stable effect-dependency key for the selection. REQUIRED: `months` is a fresh
  // array identity every render (so it can't go in a dep list), and the derived
  // `month` collapses to '' for ANY multi-selection — it cannot tell [Jul,Jun]
  // from [Jul,May], so keying reloads off `month` would silently skip the refetch.
  const monthsKey = months.join(',');
  // The In Bank Summary follows the month being viewed, but unlike `month` it is
  // never cleared by a search (which blanks `month` to span all months). So while
  // searching, the summary stays pinned to the current/selected month instead of
  // collapsing to an all-months aggregate.
  const [summaryMonth, setSummaryMonth] = useState(getCurrentMonth());
  /* [REGION-TOGGLE 2026-08-19 → REMOVED 2026-08-31] `bankRegion` lived here, choosing which
     side of the In Bank Summary showed. The summary now prints Overall, USA and ROW as three
     rows at once, so there is nothing left to switch between. */
  const [account, setAccount] = useRestorableState('payments.account', '');
  // [TAG-FILTER 2026-08-21] '' = All Tags. Restorable like the filters around it.
  const [tagFilter, setTagFilter] = useRestorableState('payments.tagFilter', '');
  const [filters, setFilters] = useState<FiltersResponse>({ accounts: [], months: [] });
  // [ADD-PAYMENT 2026-06-29] Unscoped account list — EVERY account across all months.
  // The page's Account FILTER stays month-scoped (filters.accounts), but the Add Payment
  // dropdown uses this so you can assign any existing account (e.g. "Bank Account")
  // regardless of whether it has a payment in the month currently in view.
  const [allAccounts, setAllAccounts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const silentRef = useRef(false);

  // (Previously stored client-side website overrides in sessionStorage.
  // Removed — the DB is now the only source of truth for website. Saves go
  // straight to the API, then we refetch to pick up the propagated state.)

  // Wipe any leftover override from older sessions so it can't pollute display.
  useEffect(() => {
    try { sessionStorage.removeItem('paymentsWebsiteOverrides'); } catch {}
  }, []);

  // In Bank summary
  const [bankSummary, setBankSummary] = useState<InBankSummary>({
    retention: 0, recurring_sale: 0, one_time_sale: 0,
    recurring_upsell: 0, one_time_upsell: 0, pia_amount: 0, row_total: 0, recovery: 0, disputes_won: 0, refunds_disputes: 0, total: 0, amount_total: 0, this_month_refunds_disputes: 0,
    row_recurring_sale: 0, row_one_time_sale: 0, row_recurring_upsell: 0, row_one_time_upsell: 0,
    row_pm_retention: 0,
    row_pia_amount: 0, // [ROW-PIA 2026-08-25]
    row_this_month_refunds_disputes: 0, row_refunds_disputes: 0, row_disputes_won_recovery: 0,
    // [3-ROW SUMMARY 2026-08-31] The USA / ROW rows' extra inputs.
    row_amount_total: 0, row_disputes_won: 0, row_recovery: 0,
    shopify_amount: 0, row_shopify_amount: 0, // [SHOPIFY 2026-10-19]
  });
  // [LOAD-STATES 2026-09-29, user] A failed load used to leave the zeros above and an empty table on
  // screen, which read as "$0.00 in the bank / no payments". These flags make the boxes show "—" and
  // the table show the "Couldn't load payments" countdown card (it retries by itself) instead.
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // [IN-BANK TOOLTIPS 2026-06-24] Which summary tile (if any) is being hovered,
  // plus its on-screen rect, so InBankInfoTip can anchor the themed capsule.
  // A short hover dwell (matching the app-wide GlobalTooltip's 600ms) keeps the
  // tip feeling deliberate and stops every tile flashing as the cursor sweeps
  // across the strip.
  const [inBankTip, setInBankTip] = useState<InBankTip | null>(null);
  const inBankTipTimer = useRef<number | null>(null);
  useEffect(() => () => { if (inBankTipTimer.current != null) window.clearTimeout(inBankTipTimer.current); }, []);
  /* [PHONE-LAYOUT 2026-10-22] Phones only: the summary scrolls with the page, so a tip a tap opened closes on scroll
     instead of floating over the rows. [PHONE-LAYOUT 2026-10-23] It is the page (Container) that scrolls now. */
  const hideInBankTipOnScroll = useCallback(() => {
    if (inBankTipTimer.current != null) {
      window.clearTimeout(inBankTipTimer.current);
      inBankTipTimer.current = null;
    }
    if (inBankTip) setInBankTip(null);
  }, [inBankTip]);

  // Mapping modal
  const [mappingRow, setMappingRow] = useState<Payment | null>(null);

  // Add Payment modal
  const [showAddModal, setShowAddModal] = useState(false);

  // Deal creation flow. [DEAL-CONFIRM 2026-06-23] A deal-worthy mapping save opens
  // the DealCreationModal directly (no separate "create a deal?" yes/no step), so
  // these two pieces of state are all it needs — the payment + the saved fields.
  const [dealPromptPayment, setDealPromptPayment] = useState<Payment | null>(null);
  const [dealPromptFields, setDealPromptFields] = useState<Record<string, any>>({});

  // [MOTION-A 2026-08-20] Keep each modal mounted for its exit animation after its state
  // clears (see hooks/usePresence). `value` holds the record the modal was opened with
  // through the exit, so it never flashes empty while animating away. The state variables
  // above remain the single source of truth for "is it open".
  // [MOTION-B 2026-08-20] The row that was just saved, so the table can bloom it briefly.
  // A ref-backed timer clears it; re-saving the same row restarts the bloom (the id is set
  // to null for one tick first, otherwise React sees no state change and the CSS animation,
  // already at its end state, would not replay).
  const [flashId, setFlashId] = useState<number | null>(null);
  const flashTimer = useRef<number | null>(null);
  const flashRow = useCallback((id: number) => {
    if (flashTimer.current != null) window.clearTimeout(flashTimer.current);
    setFlashId(null);
    window.setTimeout(() => setFlashId(id), 0);
    flashTimer.current = window.setTimeout(() => { setFlashId(null); flashTimer.current = null; }, 1400);
  }, []);
  useEffect(() => () => { if (flashTimer.current != null) window.clearTimeout(flashTimer.current); }, []);

  // [FLUID-3 2026-10-12] Each window is held mounted for exactly its 0.18 s close (fluid.dialogOutMs — the same
  // number durationMs.fast was, now tied to the window recipe so the two can never drift apart).
  const mappingPres = usePresence(mappingRow, fluid.dialogOutMs);
  const dealPres = usePresence(dealPromptPayment, fluid.dialogOutMs);
  const addPres = usePresence(isAdmin && showAddModal, fluid.dialogOutMs);

  // Inline edit — one cell at a time. editingField tracks which column is open.
  type EditField = 'website' | 'email';
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<EditField | null>(null);
  /* [SAVE-QUEUE 2026-09-09, user] Every write on this page — website/email edits and mapping
     saves — goes through one ordered queue (hooks/useSaveQueue, shared with Inventory).
     This REPLACES `savingRef`, which used to make a second edit to the same cell, made while
     the first was still saving, return early with no message at all: a silently lost change.
     Now it waits its turn and lands. `ahead` says how many writes precede a new one; >0 says
     "added to queue" at once. "updated" is said only when the write actually lands (see
     [TRUTHFUL TOAST 2026-09-14] in save below). */
  const { enqueue: enqueueWrite } = useSaveQueue();
  const enqueueSave = <T,>(job: () => Promise<T>, what = 'Change') => {
    const r = enqueueWrite(job);
    if (r.ahead > 0) queuedToast(what, r.ahead);
    return r;
  };
  // The cell's value at the moment editing began — used to detect whether the
  // user actually changed anything (so clicking through cells never writes).
  const editOriginalRef = useRef('');

  // Each load() call gets a unique ID; saving increments this to discard any in-flight responses.
  const loadRequestIdRef = useRef(0);
  // Holds the latest `load` so save() (defined above `load`) can trigger a silent
  // refetch without putting `load` in its dependency array — referencing `load`
  // directly here would be a use-before-initialization. Synced via the effect below.
  const loadRef = useRef<() => Promise<void>>(async () => {});
  // Same pattern for the In Bank summary. Every payment mutation re-allocates the
  // (website, month) bucket on the backend (ROW / Retention / mapped categories),
  // which changes the In Bank figures — so EVERY mutation path must refresh the
  // summary too, not just the table. Going through this ref means new mutation
  // handlers can refresh the summary without caring where loadBankSummary is
  // defined, so the summary can't silently go stale again.
  const loadBankSummaryRef = useRef<() => void>(() => {});
  // (website, month) → all mapped siblings, rebuilt on every load. Read when the
  // Edit Mapping modal opens so it can show how prior payments for the same
  // site/month were already mapped. A ref (not state) — only consumed on open,
  // so it never needs to trigger a re-render of its own.
  const bucketSiblingsRef = useRef<Map<string, Payment[]>>(new Map());

  const startEdit = useCallback((row: Payment, field: EditField) => {
    // [ACCESS 2026-07-20] Website AND email are editable by anyone with Payments-tab
    // access — the only restriction is the previous-month lock below (non-admins).
    if (isMonthLocked(row)) return;
    // Already editing this exact cell — leave it alone (the input owns its draft).
    if (editingId === row.id && editingField === field) return;
    const initial = ((field === 'website' ? row.website : row.email) ?? '') as string;
    setEditingId(row.id);
    setEditingField(field);
    editOriginalRef.current = initial;
  }, [editingId, editingField, isMonthLocked]);

  const cancelEdit = useCallback(() => { setEditingId(null); setEditingField(null); }, []);

  // `committed` is the final text from <InlineEditInput> (Enter / blur). The
  // in-progress text now lives in that child's local state, so the parent only
  // sees it here — which is what stops the table re-rendering on every keystroke.
  const save = useCallback(async (committed: string) => {
    if (editingId === null || editingField === null) return;
    const id = editingId;
    const field = editingField;
    const val = committed.trim();
    const newVal = val === '' ? null : val;
    // No change → just close. Clicking through cells without editing must not
    // fire a save (or a toast). Compared against the value captured when editing
    // began, so a background refresh can't make an untouched cell look changed.
    if (val === editOriginalRef.current.trim()) { cancelEdit(); return; }
    cancelEdit();

    // Optimistic update — only the edited row (the reference clone, if any,
    // shares the same DB id, so it updates in step).
    const original = editOriginalRef.current;
    setData((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: newVal } : r)));
    // [WRITE-LATENCY 2026-08-25] The PUT replies in ~1 s (the inventory refreshes and the
    // HubSpot deal rename run after its reply), and the value is already on screen above.
    // [TRUTHFUL TOAST 2026-09-14, user] The "updated" toast is NOT shown here any more — it
    // waits for the server (see below): the popup must mean the data is saved. A write that
    // has to wait behind another says "added to queue" at once. A failure toasts its own
    // message and puts the old value back.
    const label = field === 'website' ? 'Website name' : 'Email';
    const { promise, ahead } = enqueueSave(
      () => (field === 'website' ? updatePaymentWebsite(id, val) : updatePaymentEmail(id, val)),
      label,
    );
    try {
      const saved: any = await promise;
      /* [TRUTHFUL TOAST 2026-09-14, user] "updated" is said only once the server has confirmed.
         It used to fire the instant Save was pressed (ahead === 0), before the request returned,
         so the popup appearing and vanishing did NOT mean the save had landed — refreshing in
         that gap made the save queue's unload guard ask "Reload site? Changes you made may not
         be saved". The table still repaints instantly; only the confirmation waits. */
      toastSuccess(`${label} updated`);
      // [WRITE-LATENCY 2026-08-25] The reply says what happens to the row's HubSpot deal
      // (decided server-side, performed after the reply): an archive clears the badge now
      // rather than after the next refresh; a rename keeps it — the name change is invisible
      // here. A rename that later fails falls back to archiving, which the live refresh shows.
      if (saved?.dealAction === 'archive') {
        setData((prev) => prev.map((r) => (r.id === id
          ? { ...r, deals_record_id: null, deal_owner: null, deal_splits: null } as Payment
          : r)));
      }
      // Silent background refetch so CR-derived fields (pm_name, placed_value)
      // catch up — no loading spinner, no table flicker. The optimistic update
      // has already shown the new value to the user. The In Bank summary refreshes
      // on its own via the live-refresh bus (this PUT re-maps the bucket on the
      // backend, e.g. into ROW / Retention, which changes those totals) — that is
      // what stops the summary going stale until a full page refresh.
      silentRef.current = true;
      loadRequestIdRef.current++;
      loadRef.current().finally(() => { silentRef.current = false; });
    } catch (err) {
      console.error(`Failed to save ${field}:`, err);
      toastError(`Failed to update ${field} — the previous value is back`);
      // Put the old value straight back; the refetch below confirms it from the server.
      setData((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: original === '' ? null : original } : r)));
      silentRef.current = true;
      loadRequestIdRef.current++;
      loadRef.current().finally(() => { silentRef.current = false; });
    }
  }, [editingId, editingField, cancelEdit]);

  const baseTableColumns: Column[] = useMemo(() => baseColumns.map((col) => {
    if (col.key === 'payment_date') {
      return { ...col, render: (val: string) => {
        if (!val) return '—';
        const d = new Date(val);
        return DATE_DMY_UTC_FMT.format(d);
      }};
    }
    if (col.key === 'website') {
      return { ...col, render: (val: string, row: any) => {
        // Website is editable by anyone with access to the Payments tab — but only
        // for the current month or later. Earlier months render plain (view-only)
        // for non-admins.
        if (isMonthLocked(row)) return val || '—';
        const isEditing = editingId === row.id && editingField === 'website';
        if (isEditing) {
          return (
            <InlineEditInput
              initialValue={editOriginalRef.current}
              onCommit={save}
              onCancel={cancelEdit}
            />
          );
        }
        return (
          <span
            onClick={() => startEdit(row, 'website')}
            style={{ cursor: 'pointer', borderBottom: '1px dashed currentColor' }}
            title="Click to edit"
          >
            {val || '—'}
          </span>
        );
      }};
    }
    if (col.key === 'email') {
      return { ...col, render: (val: string, row: any) => {
        // [ACCESS 2026-07-20] Editable by anyone with Payments access — but only the
        // current month or later. Earlier months render plain (view-only). Mirrors website.
        if (isMonthLocked(row)) return val || '—';
        const isEditing = editingId === row.id && editingField === 'email';
        if (isEditing) {
          return (
            <InlineEditInput
              initialValue={editOriginalRef.current}
              onCommit={save}
              onCancel={cancelEdit}
            />
          );
        }
        return (
          <span
            onClick={() => startEdit(row, 'email')}
            style={{ cursor: 'pointer', borderBottom: '1px dashed currentColor' }}
            title="Click to edit"
          >
            {val || '—'}
          </span>
        );
      }};
    }
    if (col.key === 'amount') {
      return { ...col, render: (val: number) =>
        val != null
          ? `$${MONEY_FMT.format(Number(val))}`
          : '—',
      };
    }
    if (col.key === 'refund_dispute') {
      return { ...col, render: (_val: any, row: any) => {
        const refund = Number(row.refunded) || 0;
        const dispute = Number(row.dispute_amount) || 0;
        const total = refund + dispute;
        if (total === 0) return '—';
        return (
          <span style={{ color: '#dc2626', fontWeight: 600 }}>
            -${MONEY_FMT.format(total)}
          </span>
        );
      }};
    }
    if (col.key === 'pm_name') {
      return { ...col, render: (val: any, row: any) => {
        // [INV-SPLIT 2026-10-08, user] A split website: one PM per line, in part order ('—' for a part
        // with no PM, as the server's "A / B" has). Every other row: exactly the table's default text.
        const split = splitPartsOf(row);
        if (split.length) return split.map((p, i) => <SplitLine key={i} data-dt-cut-inner="">{p?.pm || '—'}</SplitLine>);
        return val ?? '—';
      }};
    }
    if (col.key === 'placed_value') {
      return { ...col, render: (val: any, row: any) => {
        // [INV-SPLIT 2026-10-07, user] A website split between PMs shows each PM's part, in the same
        // order as the PM Name column ("Ritika / Sana" → "$2,000.00 / $1,000.00").
        // [INV-SPLIT 2026-10-08, user] ...now one above the other: each part on its own line, level
        // with its PM's line in the PM Name cell.
        const split = splitPartsOf(row);
        if (split.length) return split.map((p, i) => <SplitLine key={i} data-dt-cut-inner="">{`$${MONEY_FMT.format(Number(p?.amount) || 0)}`}</SplitLine>);
        const parts = Array.isArray(row?.split_parts) ? row.split_parts : [];
        if (parts.length) return parts.map((p: any) => `$${MONEY_FMT.format(Number(p.amount) || 0)}`).join(' / ');
        if (val == null) return '—';
        return `$${MONEY_FMT.format(Number(val))}`;
      }};
    }
    if (col.key === 'edit_data') {
      return { ...col, render: (_val: any, row: any) => {
        // Disabled only when the payment is fully neutralized *within its own
        // month*. We use the same month-aware figures as MappingModal — the
        // API's `refunded` (same charge+account+month, month_status='TRUE')
        // plus `dispute_amount` (same charge+month, month_status='TRUE') —
        // NOT the stored `final_amount`, which subtracts refunds/disputes from
        // ANY month and would wrongly lock a row whose refund/dispute landed in
        // a later month (e.g. a May payment refunded in June).
        const amt = Number(row.amount) || 0;
        const neutralized = Number(row.refunded || 0) + Number((row as any).dispute_amount || 0);
        const finalAmt = Math.max(0, amt - neutralized);
        const fullyNeutralized = amt > 0 && finalAmt <= 0;
        // Previous months are read-only for non-admins — the Edit Data popup is
        // a write path (mapping save), so disable it there too.
        const locked = isMonthLocked(row);
        // [EDIT-DATA-GUARD] 2026-06-23 — a payment can only be mapped once it has
        // a website AND an email to attribute it to. If either cell is blank,
        // disable Edit Data and explain on hover so the user fills the missing
        // cell(s) first rather than opening an un-mappable modal.
        const websiteMissing = !String(row.website ?? '').trim();
        const emailMissing = !String(row.email ?? '').trim();
        const missingContact = websiteMissing || emailMissing;
        const disabled = fullyNeutralized || locked || missingContact;
        // Most binding reason first, so the message is always actionable: the
        // "add website/email" guidance only surfaces on rows that are otherwise
        // editable (not month-locked, not fully refunded).
        let reason: string | undefined;
        if (locked) {
          reason = 'Previous months are read-only. Mapping can only be edited from the current month onward.';
        } else if (fullyNeutralized) {
          reason = 'This payment is fully refunded/disputed this month, so there is nothing left to map.';
        } else if (missingContact) {
          reason = websiteMissing && emailMissing
            ? 'Website and email are not available for this payment. Add them in their cells first to map it.'
            : websiteMissing
              ? 'Website is not available for this payment. Add it in the Website cell first to map it.'
              : 'Email is not available for this payment. Add it in the Email cell first to map it.';
        }
        return (
          <EditBtnWrap $disabled={disabled} title={disabled ? reason : undefined}>
            <EditBtn disabled={disabled} onClick={() => !disabled && setMappingRow(row)}>
              Edit Data
            </EditBtn>
          </EditBtnWrap>
        );
      }};
    }
    if (col.key === 'tags') {
      return { ...col, render: (_val: any, row: any) => {
        const tags: React.ReactNode[] = [];
        // [ROW-TAG 2026-08-21] Leads the tag list: row_status='ROW' is stamped automatically
        // from the deal owner's Team ROW - Sales membership (routes/hubspot.ts ROW-AUTO), and
        // this is what routes the money to the ROW tile/panel and EOM's ROW - Sales row — so
        // it should be visible at a glance, not only inside Edit Mapping.
        // [ROW-PM-TAG 2026-08-25] ...and the PM side (server `row_pm`) wears the same badge, so
        // the Tags filter's 'ROW' finds the whole of Team ROW. Sides are told apart in the title.
        {
          const sales = isRowSales(row); // [ROW-TAG-ANYWHERE 2026-09-29] stamp OR a Team ROW - Sales member
          const pm = isRowPm(row);
          if (sales || pm) {
            tags.push(<RowStatusTag key="row_status" side={sales && pm ? 'both' : sales ? 'sales' : 'pm'} />);
          }
        }
        // HubSpot deal indicator — shown once a deal exists for this payment
        // (deals_record_id holds one or more comma-separated HubSpot deal IDs).
        // [HS-DEAL-LINK] 2026-06-23 — now clickable: opens the deal in HubSpot
        // (single), or a popover to pick one (multiple). Owners (comma-joined,
        // same order) label each entry in the multi-deal popover.
        if (row.deals_record_id) {
          // [DEAL-REUSE 2026-11-03, user] Each deal once: a payment holding one deal twice said "2 deals" for ONE deal.
          const dealIds = uniqueDealIds(row.deals_record_id);
          // [DEAL-OWNER-BY-ID 2026-08-24] By deal id via deal_splits, positional only as a
          // fallback — see ownersForDealIds. Still index-aligned to dealIds, so the tag
          // component's contract is unchanged.
          const owners = ownersForDealIds(row, dealIds);
          if (dealIds.length > 0) {
            tags.push(
              <HubspotDealTag key="deal_created" dealIds={dealIds} owners={owners} />
            );
          }
        }
        // [INV-SPLIT 2026-10-07, user] A split website's retention is shown BY PM ("Retention · Ritika
        // $2,000", "Retention · Sana $1,000") in the department colour it was mapped in, instead of one
        // department total. Unmapped on a split website → a quiet "Split website" note instead.
        const splitRow = Array.isArray(row.split_parts) && row.split_parts.length > 0;
        const shares: { pm: string; amount: number }[] = Array.isArray(row.retention_pm_splits) ? row.retention_pm_splits : [];
        const retField = MAPPING_FIELDS.find((f) => f.key.endsWith('_retention') && Number(row[f.key]) > 0);
        const byPm = splitRow && retField && shares.length > 0;
        // Mapping field tags
        // [TAG-STYLE 2026-11-03] Coloured by the type of money (tagKindOf); an old brand is named after the amount; a
        // brand's PIA Amount / Month / Dept are ONE tag, placed where its first PIA field is.
        const piaDone = new Set<string>();
        MAPPING_FIELDS
          .filter((f) => row[f.key] != null && row[f.key] !== '' && row[f.key] !== 0)
          .filter((f) => !(byPm && f.key.endsWith('_retention')))
          .forEach((f) => {
            if (f.label.startsWith('PIA')) {
              if (!piaDone.has(f.dept)) { piaDone.add(f.dept); tags.push(piaTag(row, f.dept)); }
              return;
            }
            const display = f.isMoney ? tagMoney(row[f.key]) : String(row[f.key]);
            tags.push(
              <TagBadge key={f.key} kind={tagKindOf(f.label)} label={f.label} value={display} note={OLD_DEPT_NAME[f.dept]} />
            );
          });
        if (byPm) {
          // [TAG-STYLE 2026-11-03] "RETENTION $2,000 · Ritika": the PM's name is the small words after the amount.
          shares.forEach((s) => tags.push(
            <TagBadge key={`ret_pm_${s.pm}`} kind="retention" label="Retention" value={tagMoney(s.amount)}
              note={[s.pm, OLD_DEPT_NAME[retField!.dept]].filter(Boolean).join(' · ')} />
          ));
        } else if (splitRow && !retField) {
          // [TAG-STYLE 2026-11-03] Dashed edge: still something to do.
          tags.push(
            <TagBadge key="split_site" kind="split" label="Split website" note="map it by PM" dashed
              title={`Split between ${row.pm_name || 'PMs'} — retention is mapped by PM: in Edit Data, click a Retention box and enter each PM's amount`} />
          );
        }
        // Recovery — single non-department field, rendered as its own tag.
        if (row.recovery != null && row.recovery !== '' && Number(row.recovery) !== 0) {
          tags.push(
            <TagBadge key="recovery" kind="other" label="Recovery" value={tagMoney(row.recovery)} />
          );
        }
        // [SHOPIFY 2026-10-19] Shopify — single non-department field, its own tag like Recovery.
        // [TAG-STYLE 2026-11-03] With "$" now, like every other amount (Shopify payouts are dollars).
        if (row.shopify_amount != null && row.shopify_amount !== '' && Number(row.shopify_amount) !== 0) {
          tags.push(
            <TagBadge key="shopify" kind="other" label="Shopify" value={tagMoney(row.shopify_amount)} />
          );
        }
        if (tags.length === 0) return '—';
        return <TagsWrapper>{tags}</TagsWrapper>;
      }};
    }
    return col;
  }), [editingId, editingField, startEdit, save, cancelEdit, isMonthLocked]);

  // [EDIT-COLUMNS 2026-09-28, user] The admin's extra columns go right after Placed Value. Read-only;
  // the header is the exact database column name. A name that clashes with a column the table
  // already shows is skipped.
  const columns: Column[] = useMemo(() => {
    const taken = new Set(baseTableColumns.map((c) => c.key));
    const accepted = acceptedExtraCols ? new Set(acceptedExtraCols) : null;
    const extra: Column[] = activeExtraCols
      .filter((name) => !taken.has(name) && (!accepted || accepted.has(name)))
      .map((name) => ({
      key: name,
      label: EXTRA_COL_LABELS[name] || name,
      align: 'center' as const,
      render: (v: any) => <ExtraCell value={v} />,
    }));
    if (!extra.length) return baseTableColumns;
    const at = baseTableColumns.findIndex((c) => c.key === 'placed_value') + 1;
    return [...baseTableColumns.slice(0, at), ...extra, ...baseTableColumns.slice(at)];
  }, [baseTableColumns, activeExtraCols, acceptedExtraCols]);

  // Load filter options, scoped to the selected month. The Account dropdown only
  // lists accounts that actually have payments in the month in view, so it
  // re-fetches whenever the month changes. (An empty month — only reached via an
  // active search — falls back to the full, unscoped account list.) If the
  // currently-selected account isn't present in the newly-scoped month, it drops
  // back to "All Accounts" so the table can't get stuck on an account with no rows.
  useEffect(() => {
    fetchFilters(month || undefined)
      .then((f) => {
        setFilters(f);
        setAccount((prev) => (prev && !f.accounts.includes(prev) ? '' : prev));
      })
      .catch((err) => console.error('Failed to load filters:', err));
  }, [month]);

  // [ADD-PAYMENT 2026-06-29] The full (month-independent) account list for the Add
  // Payment dropdown — fetched once with no month so it lists every account.
  useEffect(() => {
    fetchFilters()
      .then((f) => setAllAccounts(f.accounts))
      .catch(() => { /* non-critical — Add Payment falls back to the scoped list */ });
  }, []);

  // Default the month filter to the latest month that has data: the current
  // month once its data lands, otherwise the most recent prior month. Polls so
  // it auto-advances to the current month the moment that data appears, without
  // clobbering an older month the user has deliberately navigated to (it only
  // moves the filter forward when a newer month with data shows up).
  const latestSeenMonthRef = useRef<string | null>(null);
  useEffect(() => {
    let active = true;
    // [MULTI-MONTH 2026-07-15] Param renamed available[] — it used to be `months`,
    // which now SHADOWS the multi-select state of the same name. Auto-advance still
    // snaps to exactly ONE month (the newest with data), same as before.
    const apply = (available: string[]) => {
      if (!active) return;
      const target = latestDataMonth(available);
      if (latestSeenMonthRef.current === null || target > latestSeenMonthRef.current) {
        latestSeenMonthRef.current = target;
        setMonths([target]);
        setSummaryMonth(target);
      }
    };
    const poll = () => fetchFilters().then((f) => apply(f.months)).catch(() => {});
    poll();
    // Skip the tick while the tab is hidden — an idle/background tab shouldn't query.
    const id = setInterval(() => { if (document.hidden) return; poll(); }, 60000);
    return () => { active = false; clearInterval(id); };
  }, []);

  // [TAG-FILTER 2026-08-21] Filtering happens in the BROWSER: the page fetches every row of
  // the selected month(s) (pageSize 0, no pagination), so the full dataset is already here and
  // a client-side filter is exact, instant, and needs no server change. Month/account/search
  // stay server-side exactly as before; this only narrows what is rendered.
  const visibleData = useMemo(() => {
    if (!tagFilter) return data;
    return data.filter((r) => {
      const t = rowTagSet(r);
      return tagFilter === NO_TAGS ? t.size === 0 : t.has(tagFilter);
    });
  }, [data, tagFilter]);

  // Only the tags actually present in the loaded selection are offered - change the month and
  // the list re-derives itself. Fixed canonical order, never alphabetical-by-accident.
  const tagOptions = useMemo(() => {
    const present = new Set<string>();
    let untagged = false;
    for (const row of data) {
      const t = rowTagSet(row);
      if (t.size === 0) untagged = true;
      else t.forEach((x) => present.add(x));
    }
    const opts: string[] = TAG_FILTER_ORDER.filter((t) => present.has(t));
    if (untagged) opts.push(NO_TAGS);
    return opts;
  }, [data]);

  // A restored/stale selection whose tag no longer exists in the new month resets to All Tags
  // rather than silently filtering everything out. Guarded on data.length so the transient
  // empty dataset on first mount can't wipe a restored selection before the rows arrive.
  useEffect(() => {
    if (tagFilter && data.length > 0 && !tagOptions.includes(tagFilter)) setTagFilter('');
  }, [tagFilter, tagOptions, data.length, setTagFilter]);

  // Load In Bank summary when month/account changes
  const loadBankSummary = useCallback(() => {
    fetchInBankSummary({
      month: summaryMonth || undefined,
      account: account || undefined,
    })
      .then((s) => { setBankSummary(s); setSummaryFailed(false); })
      .catch((err) => { console.error('Failed to load bank summary:', err); setSummaryFailed(true); }); // [LOAD-STATES 2026-09-29]
  }, [summaryMonth, account]);

  useEffect(() => {
    // Keep the ref pointing at the latest loadBankSummary (which closes over the
    // current summaryMonth/account) so mutation handlers refresh the summary for
    // exactly the month/account in view.
    loadBankSummaryRef.current = loadBankSummary;
    loadBankSummary();
  }, [loadBankSummary]);

  // Live refresh: any successful write (website/email edit, mapping, add payment,
  // …) refreshes the In Bank summary so its figures never lag the table. Going
  // through the bus means future mutations are covered automatically — no handler
  // has to remember to refresh the summary.
  useEffect(() => onDataChange(() => loadBankSummaryRef.current()), []);

  // [LOAD-STATES 2026-09-29, user] A failed summary retries by itself every 10 s (not while the
  // browser tab is hidden), so its "—" boxes fill in as soon as the server answers again.
  useEffect(() => {
    if (!summaryFailed) return undefined;
    const id = window.setInterval(() => { if (!document.hidden) loadBankSummaryRef.current(); }, 10000);
    return () => window.clearInterval(id);
  }, [summaryFailed]);

  // Load payments
  const load = useCallback(async () => {
    const reqId = ++loadRequestIdRef.current;
    // [EDIT-COLUMNS 2026-09-28] A change of extra columns reloads quietly too (see applyExtraCols).
    const quiet = silentRef.current || extraColsSilentRef.current;
    extraColsSilentRef.current = false;
    if (!quiet) setLoading(true);
    try {
      const res = await fetchPayments({
        page: 1,
        pageSize: 0,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
        account: account || undefined,
        // [MULTI-MONTH 2026-07-15] Send the whole selection; [] omits the param
        // (= All Months). Server keeps `month = ?` for a single month, so the
        // common case is byte-identical to before.
        months: months.length ? months : undefined,
        search: search || undefined,
        // [EDIT-COLUMNS 2026-09-28] admin-picked extra columns (validated server-side)
        extraCols: activeExtraCols.length ? activeExtraCols : undefined,
      });

      // Build a (website, month) → all mapped siblings map. Sourced from BOTH
      // this page's mapped rows and res.references (mapped siblings the active
      // filter would otherwise hide). Both are real DB rows — we never invent
      // one. Deduped by id. The Edit Mapping modal reads this to show how prior
      // payments for the same site/month were already mapped, so the user can
      // map a new unmapped payment consistently. Mapped rows are NOT pulled out
      // of their natural slot anymore — the table just renders every row in its
      // own position/colour, so the unmapped list reads uniformly instead of
      // alternating red/green with injected reference rows.
      const siblings = new Map<string, Payment[]>();
      const seenSiblingIds = new Set<number>();
      const addSibling = (row: Payment) => {
        if (row.mapped_status !== 'TRUE' || !row.website || !row.month_str) return;
        if (seenSiblingIds.has(row.id)) return;
        seenSiblingIds.add(row.id);
        const k = bucketKey(row);
        const arr = siblings.get(k);
        if (arr) arr.push(row); else siblings.set(k, [row]);
      };
      for (const row of res.data) addSibling(row);
      for (const row of res.references || []) addSibling(row);

      // Discard this response if a save (or newer load) happened while we were
      // fetching. Crucially we DON'T clear `loading` here — a superseded load
      // flipping it off would briefly render "No records found" (empty + not
      // loading) before the winning load fills the table. The winning load
      // clears `loading` together with setData below, so the spinner stays up
      // continuously (this is what caused the flash on tab switch / StrictMode
      // double-mount).
      if (reqId !== loadRequestIdRef.current) return;

      // [EDIT-COLUMNS 2026-09-28] Which extra columns the server accepted (admins only; absent = unknown).
      setAcceptedExtraCols(activeExtraCols.length ? (Array.isArray(res.extraCols) ? res.extraCols : null) : []);
      bucketSiblingsRef.current = siblings;
      setData(res.data);
      setPagination(res.pagination);
      setLoadFailed(false); // [LOAD-STATES 2026-09-29]
      setLoading(false);
    } catch (err) {
      if (reqId !== loadRequestIdRef.current) return;
      if (!silentRef.current) console.error('Failed to load payments:', err);
      // [LOAD-STATES 2026-09-29] A QUIET refresh (live update, after a save) that fails keeps the rows
      // on screen: they are still the right month, just not the newest. Any other failed load means
      // the rows no longer match the filters, so the table shows the countdown card instead.
      if (!quiet) setLoadFailed(true);
      setLoading(false);
    }
    // [MULTI-MONTH 2026-07-15] keyed on monthsKey, NOT the derived `month` — see
    // its definition: `month` is '' for every multi-selection and so can't tell
    // one multi-selection from another, which would skip the reload.
  }, [sort.sortBy, sort.sortOrder, account, monthsKey, search, activeExtraCols]);

  // Keep loadRef pointing at the latest `load` so save() can fire a silent
  // refetch without depending on `load` (which is defined after it).
  useEffect(() => { loadRef.current = load; }, [load]);

  // Initial load + reload on filter/sort/page change
  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh every 5 minutes (silent — no loading spinner). Refreshes the
  // In Bank summary in lockstep so the figures track the table even when a
  // change is made elsewhere (another user, a background sync).
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return; // idle/background tab shouldn't refetch
      silentRef.current = true;
      load().finally(() => { silentRef.current = false; });
      loadBankSummaryRef.current();
    }, 300000);
    return () => clearInterval(interval);
  }, [load]);

  // [REALTIME] 2026-06-23 — same silent reload the moment the server signals a
  // payments-affecting DB change (sync, another user, refunds/disputes), instead
  // of waiting up to 5 min for the poll above.
  useRealtime(['payments'], () => {
    silentRef.current = true;
    load().finally(() => { silentRef.current = false; });
    loadBankSummaryRef.current();
  });

  const handleSort = useCallback((column: string) => {
    setSort((prev) => ({
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'desc' ? 'asc' : 'desc',
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Stable row-tint function so the memoized DataTable can skip re-renders when
  // the parent re-renders for an unrelated reason (toast, modal toggles, etc.).
  const rowBgFn = useCallback((row: any) => {
    const red = isDark ? 'rgba(220, 38, 38, 0.18)' : 'rgba(220, 38, 38, 0.11)';
    const status = row.mapped_status;
    if (status === 'FALSE') return red;
    // [DEAL-MISSING 2026-08-05] A fully-mapped row still reads RED when it was mapped to a
    // deal slot but no HubSpot deal exists for it — the money is allocated, the deal isn't
    // there yet. Same red as unmapped on purpose: both mean "this row needs someone".
    // The orange "Deal Created" tag is what tells the two apart at a glance.
    if (status === 'TRUE') {
      return needsDeal(row) ? red : (isDark ? 'rgba(68, 181, 123, 0.20)' : 'rgba(19, 183, 144, 0.14)');
    }
    return undefined;
  }, [isDark]);

  /* [WRITE-LATENCY 2026-08-25] The server's ORDER BY, applied in the browser so a row that
     just changed state moves to its new place NOW (the FLIP glide in DataTable carries it)
     instead of after a ~1 s reload. Search results keep the server's month/date order and are
     never regrouped, exactly like the list route. */
  const reorder = useCallback(
    (rows: Payment[]): Payment[] => (search ? rows : (resortRows(rows, sort.sortBy, sort.sortOrder) as Payment[])),
    [search, sort.sortBy, sort.sortOrder],
  );

  const handleMappingSave = async (id: number, fields: Record<string, any>) => {
    /* [WRITE-LATENCY 2026-08-25] OPTIMISTIC SAVE. Measured: the mapping PUT took 2.2-2.7 s
       (every DB round trip is ~270 ms and the route chained ten of them), and only THEN did
       the modal close, the row turn green and — a further ~1 s reload later — move down into
       the mapped section. Everything the user needs to see is known before the request is
       sent: the mapped columns are the payload, the colour follows the DB's own rule
       (total_amount = final_amount, mirrored in optimisticMappedStatus) and the position
       follows the same needs-attention order the server sorts by. So the modal closes and the
       row recolours + glides at once; the reply then reconciles the row (server-computed
       columns, deal state, the PM/placed value from inventory) and patches any sibling whose
       retention the allocator moved (`siblings`). No list reload: the live refresh confirms
       the position within seconds, and the bank summary refreshes through the write bus.
       On a rejection (e.g. retention over the placed value) the row reverts, the server's
       message toasts, and the modal reopens holding the values that were typed. */
    const before = data.find((r) => r.id === id);
    const optimistic: any = before ? { ...before, ...fields } : null;
    if (optimistic) optimistic.mapped_status = optimisticMappedStatus(optimistic);

    /* Check if any deal-worthy amounts were entered (sale or upsell fields).
       [PIA-DEAL 2026-09-09] 'pia_amount' joins them. This list is a SEPARATE gate from
       DealCreationModal's DEAL_TYPES: this one decides whether the popup opens at all, that one
       decides which cards it builds. Both must know about PIA — before this, a payment mapped as
       retention + PIA (which is what every August 2026 PIA row is) produced no deal-worthy
       amount here, so the popup never opened and the PIA money never reached HubSpot. */
    const dealSuffixes = ['r_new_sale', 'o_new_sale', 'r_so', 'o_so', 'pia_amount'];
    const depts = ['ranking', 'performance', 'tech', 'creative'];
    const hasDealData = dealSuffixes.some((suf) =>
      depts.some((dept) => Number(fields[`${dept}_${suf}`]) > 0)
    );

    setMappingRow(null);
    if (optimistic) {
      loadRequestIdRef.current++; // discard any in-flight load response
      setData((prev) => reorder(prev.map((r) => (r.id === id ? optimistic : r))));
      flashRow(id); // [MOTION-B 2026-08-20] point the eye at the row that just changed
    }
    /* [DEAL-PROMPT-AT-ONCE 2026-09-02, user] Open the deal popup NOW, not after the reply.
       It used to wait for updatePaymentMapping (~1-2 s of RDS round trips), so the user saw
       the modal close, the row recolour and glide, and only THEN the "create a deal" prompt -
       "which is wrong, it should show the deals popup immediately and in the blurred
       background the row becomes green". The optimistic row already carries everything the
       modal reads (website, email, payment_date, pm_name, deal_owner, deal_splits and
       deals_record_id come from `before`; the typed buckets from `fields`), and its submit
       only sends payment.id. The reply below merges the server-reconciled fields into the
       open prompt; a rejected save closes it again before reopening the mapping modal. */
    const promptEarly = hasDealData && !!optimistic;
    if (promptEarly) {
      setDealPromptPayment(optimistic);
      setDealPromptFields(fields);
    }

    void (async () => {
      try {
        // [SAVE-QUEUE 2026-09-09] Serialised with every other write on the page. Two mapping
        // saves in the same (website, month) bucket used to run in parallel and interleave the
        // server's retention allocation; in order, each one sees the previous one's result.
        const updated = await enqueueSave(() => updatePaymentMapping(id, fields), 'Mapping').promise;
        const { siblings: rawSiblings, ...updatedRow } = updated as any;
        const siblings: { id: number; ranking_retention: number | null }[] = Array.isArray(rawSiblings) ? rawSiblings : [];
        loadRequestIdRef.current++;
        setData((prev) => reorder(prev.map((r) => {
          if (r.id === id) return { ...r, ...updatedRow };
          const s = siblings.find((x) => x.id === r.id);
          return s ? { ...r, ranking_retention: s.ranking_retention } : r;
        })));
        if (!optimistic) flashRow(id);
        loadBankSummary();

        if (hasDealData) {
          if (promptEarly) {
            // Already open on the optimistic row: merge the server's reconciled fields (the
            // PM from inventory, deal state) into it WITHOUT closing it. The modal keeps its
            // typed rows in its own useState, so this cannot wipe what the user has entered.
            setDealPromptPayment((prev) => (prev && prev.id === id ? { ...prev, ...updatedRow } : prev));
          } else {
            // No row in the list to open on optimistically - the original path: the
            // "Data Saved Successfully!" prompt doubles as the save confirmation.
            setDealPromptPayment(updatedRow);
            setDealPromptFields(fields);
          }
        } else if (isUnmapPayload(fields)) {
          // [MAP-VALIDATION] 2026-06-23 — an already-mapped payment cleared back to
          // empty (Clear → Save). Confirm the un-map specifically.
          toastSuccess('Payment unmapped successfully!');
        } else {
          // No deal follow-up — confirm the save so the user knows it completed.
          toastSuccess('Mapping saved successfully');
        }
      } catch (err: any) {
        console.error('Failed to save mapping:', err);
        if (before) {
          loadRequestIdRef.current++;
          setData((prev) => reorder(prev.map((r) => (r.id === id ? before : r))));
        }
        const d = err?.response?.data;
        toastError(d?.message || d?.error || 'Could not save the mapping');
        if (promptEarly) setDealPromptPayment(null); // the mapping did not save - nothing to make a deal for
        if (optimistic) setMappingRow(optimistic); // reopen with what was typed
      }
    })();
  };

  // [PAYMENT-DELETE 2026-07-17] Admin soft-delete from the Edit Data modal. Server marks
  // the row excluded=1 (+ deleted_at) so it drops from the table but lands in the Bin,
  // restorable. Optimistically remove the row + decrement the count, then silent-reload to
  // reconcile pagination/sort with the server. Errors propagate to the modal's own
  // try/catch (which toasts + keeps the modal open), so we don't swallow them here.
  const handleMappingDelete = async (id: number) => {
    await deletePayment(id);
    loadRequestIdRef.current++; // discard any in-flight load response
    setMappingRow(null);
    setData((prev) => prev.filter((r) => r.id !== id));
    setPagination((prev) => ({ ...prev, totalRows: Math.max(0, prev.totalRows - 1) }));
    silentRef.current = true;
    load().finally(() => { silentRef.current = false; });
    loadBankSummary();
    toastSuccess('Payment deleted — moved to the Bin');
  };

  const handleSearchChange = (val: string) => {
    setSearch(val);
    // Searching resets ALL filters to their initial "All …" state so the search
    // spans the whole section. The month filter also snaps back to the current
    // month when the search is cleared.
    setMonths(val ? [] : [getCurrentMonth()]);
    if (val) setAccount('');
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // [MULTI-MONTH 2026-07-15] GlassSelect multi mode hands back the FULL next
  // selection, already in month order. [] = "All Months".
  const handleMonthsChange = (next: string[]) => {
    setMonths(next);
    // Keep the In Bank Summary on a real single month. It is inherently a
    // per-month figure, so it only re-pins when exactly one month is ticked;
    // "All Months" and multi-select leave it where it was rather than blanking
    // it (same rule the old single-select "All Months" already followed).
    if (next.length === 1) setSummaryMonth(next[0]);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleAccountChange = (val: string) => {
    setAccount(val);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // [LOAD-STATES 2026-09-29, user] Say exactly what is empty, and offer the fix.
  const emptyMonth = months.length === 1
    ? MONTH_LONG_FMT.format(new Date(Number(months[0].slice(0, 4)), Number(months[0].slice(5, 7)) - 1))
    : '';
  const emptyState = search
    ? { icon: 'search' as const, title: `No payments match “${search}”`, hint: 'Check the spelling, or clear the search to see all payments.', action: { label: 'Clear search', onClick: () => handleSearchChange('') } }
    : tagFilter
      ? { icon: 'search' as const, title: `No payments with this tag${emptyMonth ? ` in ${emptyMonth}` : ''}`, action: { label: 'Show all tags', onClick: () => setTagFilter('') } }
      : { title: `No payments${emptyMonth ? ` in ${emptyMonth}` : ''}`, hint: 'Pick another month or account to see more.' };
  const recordCount = loadFailed ? 'Not loaded' : `${(tagFilter ? visibleData.length : pagination.totalRows).toLocaleString()} records fetched`;

  // [PHONE-LAYOUT 2026-10-22] The In Bank Summary and the table are built once here and placed in the return.
  // [PHONE-LAYOUT 2026-10-23] Both sit where they always did, on every screen (phones: the whole page scrolls instead).
  const inBankSection = (
      <InBank2Section>
        <InBank2Header>
          <InBank2Title>In Bank Summary</InBank2Title>
          {/* [NO TOGGLE 2026-08-31, user] The Overall / ROW switch and the sliding panel it drove
              were removed. The ROW row of the strip below covers the same ground and covers it
              better: the panel showed nine ROW boxes in isolation, the row shows every column
              beside its Overall and USA counterparts, so ROW can be READ AGAINST them instead of
              having to be remembered while the panel slid. */}
        </InBank2Header>
        {loading ? (
          <InBank2Stack>
            {BANK_SCOPES.map((scope) => (
              <InBank2Grid key={scope} $cols={IN_BANK_2_COLUMNS.length} $labelCol>
                <InBank2ScopeCell $scope={scope}>{BANK_SCOPE_LABEL[scope]}</InBank2ScopeCell>
                {IN_BANK_2_COLUMNS.map((label) => (
                  <InBank2Cell key={label}>
                    {scope === 'overall' && <SkeletonBox $w="58%" $h="8px" />}
                    <SkeletonBox $w="78%" $h="15px" $mt={scope === 'overall' ? '8px' : '0'} />
                  </InBank2Cell>
                ))}
              </InBank2Grid>
            ))}
          </InBank2Stack>
        ) : (
        /* [3-ROW SUMMARY 2026-08-31, user] Three rows of the same boxes, each opening with its
           own name cell. Every figure comes from bankFigures(), whose contract is
           USA + ROW = Overall in every displayed column, the derived ones included. The Overall
           row is the OLD strip unchanged: the formulas that used to be inlined here moved into
           that function term for term. Only the Overall row prints the column labels \u2014 all
           three grids share one column template, so the rows below stay aligned under them. */
        <InBank2Stack>
          {BANK_SCOPES.map((scope) => {
            const figures = bankFigures(bankSummary, scope);
            return (
              <InBank2Grid key={scope} $cols={IN_BANK_2_COLUMNS.length} $labelCol>
                <InBank2ScopeCell $scope={scope}>{BANK_SCOPE_LABEL[scope]}</InBank2ScopeCell>
                {IN_BANK_2_COLUMNS.map((label) => {
                  /* [NEED TO MAP 2026-08-31, user] "in the usa and row row in the need to map
                     column add a dash and all the need to map thing will go to overall row
                     only". Unmapped money is a whole-book figure, not a regional one \u2014 the
                     region of a payment nobody has mapped yet is exactly what is unknown \u2014 so
                     it is printed on the Overall row and dashed out on the other two.
                     \u26a0 DISPLAY ONLY. Each row's EOM still uses its own share underneath, which
                     is what keeps USA + ROW = Overall true for the EOM column. */
                  const dashed = (scope !== 'overall' && label === 'Need to Map')
                    // [SHOPIFY 2026-10-19, user] Shopify is "in the overall and in the us": the ROW row shows a dash while it holds none.
                    || (scope === 'row' && label === 'Shopify' && !figures[label]);
                  /* [TOOLTIPS ON OVERALL ONLY 2026-08-31, user] The USA and ROW rows carry no
                     hover capsule. A tooltip explains what a COLUMN means, and the column is
                     already explained one row up \u2014 repeating it three times only meant a
                     capsule popping up wherever the pointer crossed the block. */
                  const tip = scope === 'overall' ? IN_BANK_2_TOOLTIPS[label] : '';
                  return (
                    <InBank2Cell
                      key={label}
                      onMouseEnter={tip ? (e) => {
                        const anchor = e.currentTarget.getBoundingClientRect();
                        if (inBankTipTimer.current != null) window.clearTimeout(inBankTipTimer.current);
                        inBankTipTimer.current = window.setTimeout(() => setInBankTip({ text: tip, anchor }), 600);
                      } : undefined}
                      onMouseLeave={tip ? () => {
                        if (inBankTipTimer.current != null) {
                          window.clearTimeout(inBankTipTimer.current);
                          inBankTipTimer.current = null;
                        }
                        setInBankTip(null);
                      } : undefined}
                    >
                      {scope === 'overall' && <InBank2Label>{label}</InBank2Label>}
                      {/* [LOAD-STATES 2026-09-29] A failed summary load shows "\u2014", never $0.00. */}
                      <InBank2Value $red={label === 'Need to Map' && !dashed && !summaryFailed} $muted={dashed || summaryFailed}>
                        {dashed || summaryFailed ? '\u2014' : <AnimatedNumber value={figures[label]} format={fmtBank} />}
                      </InBank2Value>
                    </InBank2Cell>
                  );
                })}
              </InBank2Grid>
            );
          })}
        </InBank2Stack>
        )}
      </InBank2Section>
  );
  const paymentsTable = (
        <DataTable
          parentScroll
          fitWidth
          // [EDIT-COLUMNS 2026-09-28] Each extra column DRAWN raises the no-scroll floor by its full width
          // (220px max text + cell padding): while the table fits the screen it has no horizontal scroll
          // and the other columns keep their room; once it no longer fits, it scrolls.
          fitMinWidth={860 + (columns.length - baseTableColumns.length) * 250}
          columns={columns}
          data={visibleData}
          sort={sort}
          onSort={handleSort}
          loading={loading}
          rowBgFn={rowBgFn}
          rowFlashFn={(r) => r.id === flashId}
          // [MOBILE 2026-07-07] Keep the SAME desktop table on phones (no card view);
          // it scrolls horizontally inside TableScroll so the full Payments grid reads
          // exactly like the website. Mirrors New Sales / Refunds / Delayed Payments.
          disableMobileCards
          // [COL-RESIZE 2026-09-29, user] Drag a header's right edge to resize a column (Excel-style);
          // double-click it to go back to automatic. Remembered per person in their own browser.
          resizeStorageKey={`dt-payments-colw:${user?.id ?? 'anon'}`}
          // [LOAD-STATES 2026-09-29, user] countdown card on a failed load; a clear line when empty.
          loadFailed={loadFailed ? { what: 'payments', onRetry: () => { loadBankSummaryRef.current(); return load(); } } : undefined}
          empty={emptyState}
          highlight={search} // [SEARCH-HIGHLIGHT 2026-09-29]
        />
  );

  return (
    <Container onScroll={isMobile ? hideInBankTipOnScroll : undefined /* [PHONE-LAYOUT 2026-10-23] phones: the page scrolls */}>
      <FrozenTop>
      <Toolbar>
        <PageTitle>Payments</PageTitle>
        {/* [MOBILE 2026-07-07] Phones: search + record count share one row
            (SearchRow), and the 2 dropdowns + Add drop to their own row (Filters
            grid). Desktop: search fills the middle, count sits with the filters. */}
        {isMobile ? (
          <SearchRow>
            <SearchBar inline liquid value={search} onChange={handleSearchChange} />
            <RecordCount>{recordCount /* [LOAD-STATES 2026-09-29] */}</RecordCount>
          </SearchRow>
        ) : (
          <SearchBar inline liquid value={search} onChange={handleSearchChange} />
        )}
        <Filters>
          {!isMobile && <RecordCount>{recordCount /* [LOAD-STATES 2026-09-29] */}</RecordCount>}
          {/* [SINGLE-MONTH 2026-07-28] The month filter selects exactly ONE month —
              multi-select (added 2026-07-15) is withdrawn everywhere by request. The
              state stays a string[] so every downstream consumer (monthsKey, the API's
              repeated ?month= params, the caches) is untouched; it just never holds
              more than one entry. No "All Months" option: a month is always selected. */}
          {/* [LIQUID-GLASS 2026-11-02, user] The three filter buttons (and Edit Columns) use the liquid glass look. */}
          <FilterSelect
            liquid
            value={months[0] || ''}
            onChange={(e) => handleMonthsChange(e.target.value ? [e.target.value] : [])}
          >
            {/* [SEARCH-ALL-MONTHS 2026-08-05] A search clears the month selection so it can
                span every month (handleSearchChange). With no option matching the resulting
                empty value the browser falls back to rendering the FIRST option, so the
                control claimed "August 2026" while the results actually covered all months.
                Render the option ONLY while nothing is selected — clearing the search restores
                a real month and it disappears again, so the normal dropdown is unchanged. */}
            {months.length === 0 && <option value="">All Months</option>}
            {/* Union the ticked months with the server's list so a selected month
                is never dropped from the options (e.g. one outside the current
                filter list still renders and stays un-tickable-away). */}
            {Array.from(new Set([...months, ...filters.months]))
              .sort((a, b) => b.localeCompare(a))
              .map((m) => (
                <option key={m} value={m}>
                  {formatMonthLabel(m)}
                </option>
              ))}
          </FilterSelect>
          <FilterSelect liquid value={account} onChange={(e) => handleAccountChange(e.target.value)}>
            <option value="">All Accounts</option>
            {filters.accounts.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </FilterSelect>
          {/* [TAG-FILTER 2026-08-21] Only tags present in the loaded month(s) are offered. */}
          <FilterSelect liquid value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
            <option value="">All Tags</option>
            {tagOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </FilterSelect>
          {/* [EDIT-COLUMNS 2026-09-28, user] Admin-only: add real payments-table columns to the table. */}
          {isAdmin && <EditColumnsMenu selected={activeExtraCols} onApply={applyExtraCols} />}
          {isAdmin && (
            <AddButton onClick={() => setShowAddModal(true)}>
              <i className="fas fa-plus" style={{ fontSize: 11 }}></i> Add
            </AddButton>
          )}
        </Filters>
      </Toolbar>
      {inBankSection}
      </FrozenTop>
      <InBankInfoTip tip={inBankTip} />
      <TableScroll>
        {paymentsTable}
      </TableScroll>
      {mappingPres.mounted && mappingPres.value && (
        <MappingModal
          payment={mappingPres.value}
          references={(bucketSiblingsRef.current.get(bucketKey(mappingPres.value)) || []).filter((s) => s.id !== mappingPres.value!.id)}
          closing={mappingPres.closing}
          onClose={() => setMappingRow(null)}
          onSave={handleMappingSave}
          // [PAYMENT-DELETE 2026-07-17] Admin-only — passed only when isAdmin, so the
          // "Delete Payment" button never renders for non-admins.
          onDelete={isAdmin ? handleMappingDelete : undefined}
        />
      )}

      {/* [DEAL-CONFIRM 2026-06-23] After a deal-worthy save we go STRAIGHT to the
          detailed confirm popup — the old generic "Data Saved Successfully! Do you
          want to create a Deal?" step is gone. The popup carries its own "✓ Data
          saved" line, shows the deal's name (Deal Owner) dropdown + pipeline /
          payment type / amount (with "was X → now Y" on updates), and Cancel is
          how the user declines to create/update a deal. */}
      {dealPres.mounted && dealPres.value && (
        <DealCreationModal
          payment={dealPres.value}
          savedFields={dealPromptFields}
          closing={dealPres.closing}
          onClose={(dealIds?: string, dealOwner?: string, dealSplits?: string | null, rowStatus?: 'ROW' | null) => {
            if (dealIds && dealPromptPayment) {
              const wasUpdate = !!dealPromptPayment.deals_record_id;
              // [DEAL-SPLIT 2026-06-29] Stamp the split layout back too so a re-open of
              // this row lands on the same split cards and updates the same deal ids.
              // [WRITE-LATENCY 2026-08-25] ...and re-sort at once: a row mapped for a deal
              // stays in the needs-attention group (red) until its deal exists, so this is
              // the moment it turns green and glides down. It used to sit there until the
              // next live refresh (up to 8 s).
              // [AMAAN-ROW 2026-09-25] ...and the ROW / US answer, when the popup asked it, so the
              // ROW tag shows (or goes) on the row at once. Undefined = not asked: keep the row's flag.
              loadRequestIdRef.current++;
              setData((prev) => reorder(prev.map((r) =>
                r.id === dealPromptPayment.id
                  ? {
                      ...r,
                      deals_record_id: dealIds,
                      deal_owner: dealOwner ?? r.deal_owner,
                      deal_splits: dealSplits ?? null,
                      ...(rowStatus !== undefined ? { row_status: rowStatus } : {}),
                    }
                  : r
              )));
              flashRow(dealPromptPayment.id);
              // [AMAAN-ROW 2026-09-25] Confirm the side too, when the ROW / US question was asked.
              const side = rowStatus === undefined ? '' : (rowStatus === 'ROW' ? ' — ROW side' : ' — US side');
              toastSuccess(`${wasUpdate ? 'Deal updated successfully' : 'Deal created successfully'}${side}`);
            }
            setDealPromptPayment(null);
          }}
        />
      )}

      {addPres.mounted && (
        <AddPaymentModal
          accounts={allAccounts.length ? allAccounts : filters.accounts}
          closing={addPres.closing}
          onClose={() => setShowAddModal(false)}
          onCreated={(row) => {
            setShowAddModal(false);
            // Prepend new row + bump record count
            setData((prev) => [row, ...prev]);
            setPagination((prev) => ({ ...prev, totalRows: prev.totalRows + 1 }));
            loadBankSummary();
            // Re-fetch filters in case the new row's month/account is new
            // (month-scoped so the Account dropdown stays in sync with the view).
            fetchFilters(month || undefined).then(setFilters).catch(() => {});
            toastSuccess('Payment added successfully');
          }}
        />
      )}
    </Container>
  );
};

export default PaymentsPage;
